<template>
  <v-card-text class="text-left">
    <v-tabs v-model="tab" class="px-4 pt-4">
      <v-tab>Assistant</v-tab>
      <v-tab>Explorer</v-tab>
      <v-tab>Categories</v-tab>
      <v-tab>Use Cases</v-tab>
      <v-tab>Background</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">

      <!-- ------------ Data Assistant ------------ -->
      <v-tab-item class="pa-6">
        <v-card-text>
          <v-alert outlined type="info">
            <strong>Version 0.2</strong><br>
            <ul>
              <li>The dataset is limited to the last 800 calls at the time of the snapshot.</li>
              <li>Call context for more accurate responses</li>
              <li>Linked call IDs</li>
            </ul>
          </v-alert>
            <v-text-field v-model="userQuery" filled rounded @keyup.enter="sendQuery" append-outer-icon="mdi-send" @click:append-outer="sendQuery" :disabled="assistantLoading">
              <template v-slot:prependInner>
                <v-icon>mdi-check</v-icon>
              </template>
              <template v-slot:append>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">mdi-dots-vertical</v-icon>
                  </template>
                  <v-list>
                    <v-list-item v-for="(prompt, index) in prompts" :key="'prompt' + index" @click="userQuery = prompt.title">
                      <v-list-item-subtitle class="ellipsis text-left">{{ prompt.title | truncate }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-text-field>
            <v-divider></v-divider>
            <div class="message-history">
              <div v-if="assistantLoading" class="text-center pa-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <span class="ml-2">{{ timer }} seconds (processing will take a few minute)</span>
              </div>
              <v-slide-x-transition group class="d-flex flex-column-reverse">
                <div v-for="(message, index) in history" :key="index" class="message d-flex" :class="message.type == 'user' ? 'justify-end' : 'justify-start'">
                  <div v-if="message.type === 'user'" class="user-message pa-4 ma-2">
                    {{ message.content }}
                  </div>
                  <div v-else>
                    <div>
                      <div class="system-message pa-6 ma-2" v-html="message.content"></div>
                      <div v-if="message.citations && message.citations.length" class="citations ml-2">
                        <div v-for="(citation, citationIndex) in message.citations" :key="'citation-' + index + '-' + citationIndex" class="text-caption">
                          [{{ citationIndex + 1 }}] <a :href="citation.uri" target="_blank">{{ citation.uri }}</a> ({{ citation.startIndex }} - {{ citation.endIndex }})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-slide-x-transition>
            </div>
            <div class="d-flex justify-end">
              <v-btn v-if="history.length" @click="startConversation()" :disabled="assistantLoading" text><v-icon left>mdi-refresh</v-icon> Reset Conversation</v-btn>
            </div>
        </v-card-text>
      </v-tab-item>

      <!-- ------------ Data Explorer ------------ -->
      <v-tab-item class="pa-6">
        <v-alert outlined type="info">
          <strong>Version 0.2</strong><br>
          <ul>
            <li>Slower initial load but full dataset</li>
            <li>Search button for less lag</li>
            <li>More detailed descriptions</li>
            <li>OpenAI transcriptions</li>
          </ul>
        </v-alert>
        <v-card-text v-if="explorerLoadingState == 0" class="my-3">
          <v-btn @click="loadExplorerData" color="primary">Load Data</v-btn>
        </v-card-text>
        <v-card-text v-else-if="explorerLoadingState == 1" class="my-3">
          Loading 162,728 records (about 20 seconds)...
          <div class="mt-3">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-text v-else>
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            append-outer-icon="mdi-send"
            @click:append-outer="logQuery('explorer'); executeSearch()"
            label="Search"
            single-line
            hide-details
            filled
            class="mb-6"
            rounded
            @blur="logQuery('explorer'); executeSearch()"
            @keyup.enter="logQuery('explorer'); executeSearch()"
          ></v-text-field>
          <v-data-table
            :headers="headers"
            :items="filteredItems"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            hide-default-footer
            class="elevation-1"
            @page-count="pageCount = $event"
            @click:row="openCallDialog"
          ></v-data-table>
          <div class="text-center pt-2">
            <v-pagination
              v-model="page"
              :length="pageCount"
            ></v-pagination>
          </div>
        </v-card-text>
      </v-tab-item>

      <!-- ------------ Data Categories ------------ -->
      <v-tab-item class="pa-6">
        <v-alert outlined type="info">
          <strong>Version 0.2</strong><br>
          <ul>
            <li>CC&B Categories</li>
            <li>Limited to maximum of 100 example calls of each</li>
            <li>Data has not been sanitized</li>
          </ul>
        </v-alert>
        <v-card-text v-if="categoriesLoadingState == 0" class="my-3">
          <v-btn @click="loadCategoryData" color="primary">Load Data</v-btn>
        </v-card-text>
        <v-card-text v-else-if="categoriesLoadingState == 1" class="my-3">
          <div class="mt-3">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-text v-else>
          <v-expansion-panels>
            <v-expansion-panel v-for="(category, index) in categories" :key="'category'+index">
              <v-expansion-panel-header>
                <div class="text-h6">{{ category.name }} ({{ category.count }})</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-for="(call, index2) in category.calls" :key="'link-' + index + '-' + index2">
                  <a href="#" @click="openCallDialog({ id: call })">{{ call }}</a>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-tab-item>

      <!-- ------------ Use Cases ------------ -->
      <v-tab-item class="pa-6">
        <v-card-text>
          <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
            <ul class="text-left">
              <li>Topic Categorization</li>
              <li>Understanding Sentiment</li>
              <li>Issue Identification, Resolution, and Recommendations</li>
            </ul>
          </v-sheet>
          <!-- ------------ Proof of Concept ------------ -->
          <v-row class="pa-2">
            <v-col v-for="(usecase, index) in usecases" :key="usecase.id" cols="12" xs="12" sm="12" md="6" lg="4">
              <v-card class="mx-auto" style="height: 100%">
                <v-img class="white--text align-end" height="200px" :src="usecase.image"
                  @click="showUseCaseDialog(index)"></v-img>
                <v-divider></v-divider>
                <v-card-title>
                  {{ usecase.name }}
                </v-card-title>
                <v-card-subtitle>
                  {{ usecase.id }}
                </v-card-subtitle>
                <v-card-text class="text--primary">
                  {{ usecase.description }}
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-tab-item>
      <v-dialog v-if="selectedUseCase" v-model="useCaseDialog" width="800">
        <v-card class="text-left">
          <div class="d-flex flex-row">
            <div class="d-flex flex-column flex-2 pa-6">
              <v-card-title>
                {{ selectedUseCase.id }}
              </v-card-title>
              <v-card-text>
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>Transcript</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      {{ selectedUseCase.transcription }}
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <div class="mt-8 mb-6">
                  <h3 class="mb-6">Detailed Summary</h3>
                  {{ selectedUseCase.understanding.summary }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Topic Categorization</h3>
                  {{ selectedUseCase.understanding.category }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Sentiment</h3>
                  {{ selectedUseCase.understanding.sentiment }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Issue Identification</h3>
                  {{ selectedUseCase.understanding.issue }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Resolution</h3>
                  {{ selectedUseCase.understanding.resolution }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Recommendation</h3>
                  {{ selectedUseCase.understanding.recommendation }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Agent Performance</h3>
                  {{ selectedUseCase.understanding.performance }}
                </div>
              </v-card-text>
            </div>
            <div class="d-flex flex-column flex-1 pa-6 white--text" style="background: black">
              <h4 class="mb-6">Call Recording</h4>
              <audio ref="player" :src="selectedUseCase.recording" controls></audio>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <!-- ------------ Background ------------ -->
      <v-tab-item class="pa-6">
            <v-tabs vertical>
              <v-tab class="justify-start">
                <v-icon left>
                  mdi-lightbulb
                </v-icon>
                Idea
              </v-tab>
              <v-tab class="justify-start">
                <v-icon left>
                  mdi-briefcase-account
                </v-icon>
                Business Case
              </v-tab>
              <v-tab class="justify-start">
                <v-icon left>
                  mdi-bullseye-arrow
                </v-icon>
                Goals
              </v-tab>
              <v-tab class="justify-start">
                <v-icon left>
                  mdi-memory
                </v-icon>
                Approach
              </v-tab>
              <v-tab class="justify-start">
                <v-icon left>
                  mdi-check-decagram
                </v-icon>
                Status
              </v-tab>
              <v-tab disabled class="justify-start">
                <v-icon left>
                  mdi-flag-checkered
                </v-icon>
                Conclusion
              </v-tab>
              <!-- ------------ Idea ------------ -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <h2 class="mb-6">Idea</h2>
                    <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
                      <div class="text-h6 text-center ml-12">Can we extract useful insights from our historical call recordings?</div>
                    </v-sheet>
                    <p>
                      Hydro Ottawa maintains a large repository of call recordings from customer interactions. Over 6 years, we have recorded 2,159,738 calls.
                      If we could feed this data into a large language model then we could extract useful insights from the data that would otherwise be cost prohibitive to obtain.
                    </p>
                  </v-card-text>
                </v-card>
              </v-tab-item>
              <!-- ------------ Business Case ------------ -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <h2 class="mb-6">Business Case</h2>
                    <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
                      <ul>
                        <li class="subtitle-1">Topic Categorization</li>
                        <li class="subtitle-1">Understanding Sentiment</li>
                        <li class="subtitle-1">Issue Identification, Resolution, and Recommendations</li>
                        <li class="subtitle-1">Agent Performance and Training</li>
                        <li class="subtitle-1">Operational Efficiency & Self-serve</li>
                        <li class="subtitle-1">Predictive Analysis</li>
                      </ul>
                    </v-sheet>
                    <h3 class="mt-12 mb-6">Topic Categorization</h3>
                    <p>
                      <ul>
                        <li>Automatically classifying calls into predefined topics or categories.</li>
                        <li>Identifying recurring themes or subjects in conversations.</li>
                        <li>Facilitating targeted analysis and reporting based on call topics (including productivity/efficiency measures like average handling time)</li>
                        <li>Enabling sharpened understanding of CSAT based on customer intent (categorization) to truly understand</li>
                        <li>Identify opportunities to optimize call routing (IVR)</li>
                        <li>
                          Today: agent categorizes based on a pick list - issue, too broad, misclassification, no entries .
                          <ul>
                            <li>For example: Billing is the top category ringing in at a whopping 37% of all the calls - question is - what about billing is the inquiry about - can we isolate a true “High bill” inquiry from one that simply indicates the customer doesnt understand their bill, or its that the usage is not high, but rather they didnt pay last month's bill, or their OESP support stopped, etc..</li>
                            <li>Another example : could have multiple topics in call journey - customer called to move (thats one intent), he has to choose a rate option that requires an explanation (thats another intent - rate option selection), etc.  If we could identify and summarize the different categories/calls we could then pick out , say “rate option” and focus on those to understand whether we have effective agent articulation through a customers lens, and/or whether relevant online tools were promoted all to help inform customer.  We could develop workshops/trainig depending on the topic.</li>
                            <li>We could also expand the topics to monitor as relevant, configured, dynamic with no impact or effort on agent's part</li>
                            <li>New topics could be recommended by the AI - especially around new trends; inquiries etc</li>
                          </ul>
                        </li>
                      </ul>
                    </p>
                    <h3 class="mt-12 mb-6">Understanding Sentiment</h3>
                    <p>
                      <ul>
                        <li>Detecting customer emotions (e.g., frustration, satisfaction).</li>
                        <li>Assessing the tone and mood of both the customer and the agent.</li>
                        <li>Identifying shifts in sentiment during the call.</li>
                        <li>
                          Benefits:
                          <ul>
                            <li>Enhanced Training and Coaching: Use insights from sentiment analysis to inform agent training and coaching. Focus on areas like empathy, conflict resolution, and customer engagement.; Develop targeted training programs to improve emotional intelligence and customer handling skills of the agents. Evaluate agent performance in handling different emotional contexts and use this data to drive improvement at agent level</li>
                            <li>Improved Customer Experience and Satisfaction:Understand the factors leading to positive or negative sentiments, allowing us to refine programs, services, and customer interactions; Implement changes that proactively address common customer pain points</li>
                            <li>Feedback and Performance Analysis:Analyze sentiment trends over time to assess the impact of policy changes, new products, or marketing campaigns on customer sentiment; Evaluate agent performance in handling different emotional contexts and use this data in performance reviews and recognition programs.</li>
                            <li>Crisis Management and Brand Reputation: identify and address widespread issues or negative sentiment trends that could lead to a public relations crisis.; Protect and enhance brand reputation by demonstrating responsiveness and a customer-centric approach.</li>
                          </ul>
                        </li>
                        <li>
                          <strong>Advanced:</strong> Predictive Analytics and Proactive Solutions: Utilize patterns in sentiment data to predict potential customer issues before they escalate, allowing for proactive intervention.; Identify at-risk customers early and reach out with solutions to resolve issues or improve their experience
                        </li>
                        <li>
                          <strong>Live Sentiment Analysis</strong>
                          <ul>
                            <li>Personalized Customer Service: Live: Tailor the conversation and service approach based on the detected sentiment of the customer, leading to a more personalized and empathetic interaction.; Agents can adjust their tone and approach in real-time, aligning with the customer’s emotional state to build rapport and trust ;</li>
                            <li>Real-time Support and Intervention: Identify calls where customers are showing signs of frustration or dissatisfaction and flag them for immediate attention or escalation.; Offer real-time support to agents during challenging calls, such as providing them with prompts or guidance on how to de-escalate situations.</li>
                          </ul>
                        </li>
                      </ul>
                    </p>
                    <h3 class="mt-12 mb-6">Issue Identification, Resolution, and Recommendations</h3>
                    <p>
                      <ul>
                        <li>Detecting specific issues or problems mentioned in the conversation.</li>
                        <li>Analyzing common issues across calls to improve product or service.</li>
                        <li>Suggesting potential solutions or responses to the agent in real-time.(eventually)</li>
                        <li>Recommending promotional opportunities where appropriate.</li>
                        <li>Tailoring interactions based on customer history and preferences.</li>
                        <li>Predicting and preempting potential customer issues.</li>
                      </ul>
                    </p>
                    <h3 class="mt-12 mb-6">Agent Performance and Training</h3>
                    <p>
                      <ul>
                        <li>Analyzing agent performance for training and development purposes.</li>
                        <li>Identifying best practices and areas for improvement.</li>
                        <li>Customizing training programs based on individual agent needs.</li>
                      </ul>
                    </p>
                    <h3 class="mt-12 mb-6">Operational Efficiency & Self-serve</h3>
                    <p>
                      <ul>
                        <li>Understand reasons for repeat calls</li>
                        <li>First call resolution</li>
                        <li>self-serve opportunities</li>
                      </ul>
                    </p>
                    <h3 class="mt-12 mb-6">Predictive Analytics</h3>
                      <p>
                        <ul>
                          <li>Anticipating future trends and customer needs based on call data.</li>
                          <li>Informing strategic decisions through comprehensive analysis.</li>
                          <li>Identifying opportunities for innovation in products and services.</li>
                        </ul>
                      </p>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <!-- ------------ Goals ------------ -->
                <v-tab-item>
                  <v-card flat>
                    <v-card-text>
                      <h2 class="mb-6">Goals</h2>
                      <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
                        <ul>
                          <li class="subtitle-1">Natural Search</li>
                          <li class="subtitle-1">Conversational Search</li>
                          <li class="subtitle-1">Dashboard</li>
                          <li class="subtitle-1">Call Classification</li>
                          <li class="subtitle-1">Recommendations</li>
                          <li class="subtitle-1">Journeys</li>
                          <li class="subtitle-1">Trends</li>
                        </ul>
                      </v-sheet>
                      <h3 class="mt-12 mb-6">Natural Search</h3>
                      <p>
                        <ul>
                          <li>Query the entire dataset with natural language queries</li>
                          <ul>
                            <li>ex. how many records mention automatic payments</li>
                            <li>ex. return records that mention call quality</li>
                          </ul>
                        </ul>
                      </p>
                      <h3 class="mt-12 mb-6">Conversational Search</h3>
                      <p>
                        <ul>
                          <li>Users may directly converse with the data itself</li>
                          <ul>
                            <li color="red">Restricted to the token limit of the model</li>
                            <li>Provides an interactive chat interface </li>
                          </ul>
                        </ul>
                      </p>
                      <h3 class="mt-12 mb-6">Dashboard</h3>
                      <p>Simple graphs and lists which summarize pre-calculated data such as agent sentiment</p>
                      <h3 class="mt-12 mb-6">Call Classification</h3>
                      <p>
                        <ul>
                          <li>Classifies based on recommended categories (multiple, weighted)</li>
                          <li>Classify based on the known categories:Billing, Moves, Not Coded by Agent, Acct Maintenance, Collections, Payment, MyAccount, Operations,
                            Other, Reconnections, Pre-Authorized Payment Plan, Social Assist, Forestry, LLR, Outages, Booking, Battery Loan Inquiry, Rate Option, Document Request,
                            Surveys, MOBILE APP, Layouts, Outage Alert Inq, Scam, Usage Inq, CEAPSB, Next Level Complaint, Deposit Inquiry, Complaint & Escalations,
                            Cancelling, EV Inquiry, LD, PROMOTION, IVR Self-Service, Usage Online, CEAP, Home Energy Reports, CDM, Green Button, Supervisions
                          </li>
                        </ul>
                      </p>
                      <h3 class="mt-12 mb-6">Recommendations</h3>
                      <p>
                        <ul>
                          <li>Users may directly converse with the data itself</li>
                          <ul>
                            <li color="red">Restricted to the token limit of the model</li>
                            <li>Provides an interactive chat interface </li>
                          </ul>
                        </ul>
                      </p>
                      <h3 class="mt-12 mb-6">Journeys</h3>
                      <p>
                        Attempt to extract customer jouneys. We start by identifying call resolutions and link them to call entries.
                      </p>
                      <h3 class="mt-12 mb-6">Trends</h3>
                      <p>
                        Provide graphs for pre-defined trends topics. For example, we could provide a graph of the number of calls that mention "carbon tax" over time.
                        Ultimately, trends will be used to power an alerting system.
                      </p>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <!-- ------------ Approach ------------ -->
                <v-tab-item>
                  <v-card flat>
                    <v-card-text>
                      <h2 class="mb-6">Approach</h2>
                      <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
                        <div class="text-h6 text-center ml-12">
                          Speech-to-text, Natural Language Processing, Machine Learning
                        </div>
                      </v-sheet>
                      <p>
                        <ul>
                          <li>Obtain sample audio files</li>
                          <li>Analyze speech-to-text models</li>
                          <li>Analyze LLM models</li>
                          <li>Process sample files</li>
                          <li>Share results</li>
                          <li>Gain access to full data set</li>
                          <li>Develop pipeline</li>
                          <li>Process 1 year of data</li>
                          <li>Create interactive app</li>
                        </ul>
                      </p>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <!-- ------------ Status ------------ -->
                <v-tab-item>
                  <v-card flat>
                    <v-card-text>
                      <h2 class="mb-6">Status</h2>
                      <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
                        <div class="text-h6 text-center ml-12">Gain access to full data set</div>
                      </v-sheet>
                      <ul>
                        <li class="text-decoration-line-through">Obtain sample audio files</li>
                        <li class="text-decoration-line-through">Analyze speech-to-text models</li>
                        <li class="text-decoration-line-through">Analyze LLM models</li>
                        <li class="text-decoration-line-through">Process sample files</li>
                        <li class="text-decoration-line-through">Share results</li>
                        <li class="text-decoration-line-through">Develop pipeline</li>
                        <li>Gain access to full data set</li>
                        <li>Present to EMT</li>
                        <li>Process 1 year of data</li>
                        <li>Create interactive app</li>
                      </ul>
                      <h3 class="mt-12 mb-6">March 7, 2024</h3>
                      <p>
                        Currently, awaiting transfer of the entire data set. Exploring the possibility of using Snowflake to compute the Whisper model.
                      </p>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
                <!-- ------------ Conclusion ------------ -->
                <v-tab-item>
                  <v-card flat>
                    <v-card-text>
                      <h2 class="mb-6">Conclusion</h2>
                      <v-sheet color="grey lighten-4 pa-4 mb-6" rounded>
                        
                      </v-sheet>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs>
            </v-tab-item>
            
            <!-- ------------ Analysis ------------ -->
            <v-tab-item class="pa-6">
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>Google: Long + Gemini Pro</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription</h3>
                    <ul>
                      <li>hi this is Richard from Hydro Ottawa how can help you</li>
                      <li>and the last one I just received there was a large amount and I wondered why and then noticed that our automated withdrawal stopped all of a sudden last November</li>
                      <li>I see okay and we didn't do anything to stop that we had moved in July and we had called and got the address change and done all that</li>
                      <li>so now we've been charged some interest and I'd like to have that looked at and interest removed so we can just pay what we're owed owing what's going</li>
                      <li>okay let's see here can I see the what's the address</li>
                      <li>it's</li>
                      <li>the GRE and us</li>
                      <li>apostrophe s</li>
                      <li>and</li>
                      <li>yes okay and can I verify</li>
                      <li>the date of birth</li>
                      <li>September 3rd 1960</li>
                      <li>okay can I verify the phone number on the file</li>
                      <li>6 1 3 9 9 0 7 7 1 5 that might be one of them</li>
                      <li>so she doesn't last payment receive as we back in October it looks like it was three bills altogether but the interest honestly it wasn't that much was really a dollar 52 yeah I know but I mean the fact</li>
                      <li>we didn't make this change I don't really feel we should pay any interest I'd like to get it rectified and you know I could take the dollar fifty two off it's fine but yeah okay great look for you</li>
                      <li>you can you see why the automated which are all was</li>
                      <li>turned off</li>
                      <li>20 years plus</li>
                      <li>you</li>
                      <li>okay so it looks like</li>
                      <li>oh yeah there you go cancel the interest that's fine so now the remaining balance is 254 dollars and seventy cents</li>
                      <li>and the reason why the auto pay was canceled</li>
                      <li>yeah since it has been 2007 until 23 so yeah so in the system it always it always after so many years it stops the</li>
                      <li>the auto pay after so many years okay well there should be some sort of notice or something right when you first sign up for the autoplay on the form it says that after why that's a long time I know that's a long time to know and because it's automated payments you know it</li>
                      <li>and I kept every invoice and notice from Hydro Ottawa nothing like that just a suggestion it might be good to set up some sort of notice that tells people that</li>
                      <li>all that or do I have to make a payment oh you have to make a payment man you'll see for this one here</li>
                      <li>haha figured that okay</li>
                      <li>all right so can you reactiv</li>
                      <li>ate automated withdrawal for the next bill okay so you have to do that I can activate it so the customer has to do that okay I'm online so where do I go to do that you</li>
                      <li>okay I did it myself here okay I just in 2027</li>
                      <li>okay</li>
                      <li>okay yeah there you go okay now it's active again but then this yeah but disappearing has to be paid manually</li>
                      <li>so when will I see the revised amount so that I can pay the bill cuz that's a pretty fresh my site and it's not you get on there within give it like an hour sort of the system</li>
                      <li>okay</li>
                      <li>okay thanks very much no worries you have yourself a good one okay okay YouTube bye now</li>
                    </ul>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (Gemini Pro)</h3>
                      <strong>1. Detailed summary of the call</strong><br>
                      The customer, Richard, called Hydro Ottawa to inquire about a large bill he received. He noticed that his automated withdrawal had stopped in November, resulting in interest charges. Richard explained that he had moved in July and had updated his address with Hydro Ottawa. The customer service representative confirmed that the automated withdrawal had been canceled due to the account being inactive for over 20 years. The representative agreed to remove the interest charges and reinstated the automated withdrawal. The customer made a manual payment for the outstanding balance and was advised to check his online account within an hour to see the revised amount.<br><br>
                      <strong>2. Topic categorization</strong><br>
                      Billing<br><br>
                      <strong>3. Sentiment analysis</strong><br>
                      Overall, the sentiment of the call is positive. The customer was frustrated with the situation but the customer service representative was helpful and resolved the issue.<br><br>
                      <strong>4. Issue Identification, Resolution and recommendation</strong><br>
                      <strong>Issue:</strong> Automated withdrawal was canceled, resulting in interest charges.<br>
                      <strong>Resolution:</strong> The customer service representative removed the interest charges and reinstated the automated withdrawal.<br>
                      <strong>Recommendation:</strong> Hydro Ottawa should consider sending a notice to customers when their automated withdrawal is canceled.<br><br>
                      <strong>5. Agent Performance</strong><br>" +
                      The customer service representative was helpful and resolved the issue to the customer's satisfaction. The representative was polite and professional.<br><br>
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="warning" class="mt-6">
                      The call summary was inaccurate. The call was successfully classified as a billing inquiry. The sentiment of the call was positive, and the issue was resolved to the customer's satisfaction. The customer service representative demonstrated good performance and professionalism.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>Google: Telephony v2 + Gemini Pro</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription</h3>
                    <ul>
                      <li>hi this is Richard from Hydro Ottawa how can I help you</li>
                      <li>and um the last 1 I just received there was a large amount and I wondered why and then noticed that our automated withdrawal stopped all of a sudden uh last November</li>
                      <li>I see okay uh last and we didn't do anything to stop that we had moved in July and we had called and got the address changed and done all that</li>
                      <li>so um now we've been charged some interest and I'd like to have that looked at and interest removed so we can just pay what we're owed owing what's going</li>
                      <li>okay uh let's see here um can I see the uh what's the address</li>
                      <li>it's uh</li>
                      <li>just like g r e n s</li>
                      <li>apostrophe s okay</li>
                      <li>if you go in</li>
                      <li>yes yes</li>
                      <li>okay and can I verify uh</li>
                      <li>the date of birth</li>
                      <li>septembar 3rd 1960 okay and can I verify the phone number on the file</li>
                      <li>yeah I see there's a there was a last payment received was way back in October it looks like it was 3 bills altogether but the interest honestly it wasn't that much it was only a dollar 52 uh yeah I know but I mean the fact that we didn't make this change I don't really feel we should pay any interest I just like to get it rectified and you know I could take the I could take the dollar 52 off it's fine but uh yeah okay great that for you</li>
                      <li>do can you see why the um automated which are all was</li>
                      <li>turned off</li>
                      <li>cuz we've had this for like 20 years plus</li>
                      <li>okay</li>
                      <li>so it looks like um</li>
                      <li>all right yeah there you go I cancel the interest that's fine okay so now the remaining balance is 254.70</li>
                      <li>and the reason why the auto pay was cancelled</li>
                      <li>yeah since it has been 2007 till 2023 so yeah um so in the system and always it always after so many years it stops the uh</li>
                      <li>the auto pay after so many years okay well there should be some sort of notice or something right when you first sign up for the auto pay on the form it says that after a few uh uh while I'm at the V yeah that's a long time I know I know that's a long time to know and because it's automated payments you know it you know</li>
                      <li>and I've kept every um invoice and notice from Hydro Ottawa nothing like that just a suggestion it might be good to set up some sort of notice that tells people that</li>
                      <li>will automatically withdraw that or do I have to make a payment oh you have to make a payment manually uh for this 1 here</li>
                      <li>uh-huh figured that okay</li>
                      <li>all right so can you reactivate the automated withdrawal for the next bill</li>
                      <li>okay so you'd have to do that um I can activate it the the customer has to do that um okay I'm online so where do I go to do that okay let me see here 1 second</li>
                      <li>oh okay no no no okay I did it myself here okay I just I just now it ends in 2027</li>
                      <li>okay</li>
                      <li>okay yeah there you go okay now it's active again but then this yeah but this payment has to be paid manually</li>
                      <li>okay so when will I see the revised amount so that I can pay the bills</li>
                      <li>refresh my site and it's not</li>
                      <li>uh you should see it on there within give it like an hour or so to update the system okay okay</li>
                      <li>okay okay okay thanks very much know where is you have yourself a good 1 okay okay you too bye now bye bye</li>
                    </ul>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (Gemini Pro)</h3>
                      The customer, Richard, contacted Hydro Ottawa because he received a large bill with interest charges. He realized that his automated payment had stopped in November without his knowledge, and he wanted to have the interest removed and the automated payment reactivated. The agent investigated and confirmed that the automated payment had been deactivated because it had been active for over 20 years. The agent removed the interest charges and reactivated the automated payment. The customer was instructed to manually pay the current bill and that the updated balance would be reflected on his account within an hour.<br><br>
                      <strong>Topic Categorization</strong><br>
                      Payment<br><br>
                      <strong>Sentiment Analysis</strong><br>
                      Richard was initially frustrated due to the large bill and interest charges. However, he became more positive when the agent removed the interest and reactivated the automated payment.<br><br>
                      <strong>Issue Identification, Resolution and Recommendation</strong><br>
                      <strong>Issue:</strong> Automated payment was deactivated without the customer's knowledge, resulting in interest charges.<br>
                      <strong>Resolution:</strong> The agent removed the interest charges and reactivated the automated payment.<br>
                      <strong>Recommendation:</strong> Hydro Ottawa should consider providing a notice to customers before their automated payments are deactivated.<br><br>
                      <strong>Agent Performance</strong><br>                      
                      The agent was helpful and resolved the issue promptly. They were also able to provide additional information about automated payment deactivation.
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="warning" class="mt-6">
                      The call summary was inaccurate. The call was successfully classified as a payment inquiry. The sentiment of the call was positive, and the issue was resolved to the customer's satisfaction. The customer service representative demonstrated good performance and professionalism.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>Google: Chirp Telephony v2 + Gemini Pro</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription</h3>
                    <ul>
                      <li>hi this is richard from hydroadowa how can help you hi richard this is diane i received a a bill my bills are online my uh invoices and um the last one i just received there was a large amount and i wondered why and then noticed that our automated withdraw stopped all of a sudden uh last november i see okay uh and we didn't do anything to stop</li>
                      <li>that we had moved in july and we had called and got the address changed and done all that so um now we've been charged some interest and i'd like to have that looked at and interest removed so we can just pay what we're owed owing what's owing okay uh let's see here um can i see the uh what's the address it's um 3478 greens road</li>
                      <li>it's like g r e n s apostrophe s okay and is it in yes okay and can i verify uh the data of birth september 3rd 1960 okay and can i verify the phone number on the fl 613 990715</li>
                      <li>that might be one of them yeah i see there's a was last payment we received was way back in october it looks like it was three bills all together but the interest honestly it wasn't that much it was only $1.52 uh yeah i know but i mean the fact that we didn't make this change i don't really feel we should pay any interest i just like to get it rectified and i could take the i could take the dollar 52 off it's fine but uh yeah okay that's for you</li>
                      <li>can you see why the um automated withdraw was turned off cuz we've had this for like 20 years pluss okay okay</li>
                      <li>so looks like um all right yeah there you go i cancel the interest that's fine so now the remaining balance is $254.70 and the reason why the autoop was can't sold yeah since it has um 2007 to 2023 so yeah um so in the system it always it always after so many years it it stops the uh</li>
                      <li>the autopay after so many years okay well there should be some sort of notice or something right when you first sign up for the autoop on the form it says it after why yeah that's a long time i i know that's a long time to know and because it's automated payments you know it and i've kept every um invoice and notice from hydro autowalk nothing like that just a suggestion it might be good to set up some sort of notice but</li>
                      <li>tells people that yeah yeah okay we'll do that okay so yeah the runing balance it's uh now $254.70 okay okay so can you automatically withdraw that or do i have to make a payment oh you have to make payment manually for this one yeah uh-huh figured that okay all right so can you reactivate the automated withdraw for the next bill okay so you'd have to</li>
                      <li>do that um i can't activate it the customer has to do that um okay i'm online so where do i go to do that let's see here one second i'm not happy about that okay no never mind okay i did it myself here okay i just extended the date now to now so now it ends in 2027 okay</li>
                      <li>okay yeah there you go okay now it's active again but then this yeah but this has to be p manually okay so when will i see the revised amount so that i can pay the bill buse i just refreshed my site and it's not uh you should see it on there within give it like an hour or so to update in the system okay okay okay okay thanks very much no worries you have yourself a good one okay okay you too</li>
                    </ul>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (Gemini Pro)</h3>
                      <strong>1. Summary</strong><br>
                      Diane called Hydro Ottawa with an inquiry about a recent bill that had a large amount due. She discovered that, although she had changed her address, the automated withdrawal had stopped in November and had not been reinstated. Diane requested that the interest charge of $1.52 be removed and inquired about the reason for the automated withdrawal being turned off. Hydro Ottawa confirmed that this happened after the automated payment method had been in effect since 2007. The agent cancelled the late payment fee and reactivated the automated withdrawal for the next bill. The revised amount has not yet shown on Diane's online account, but should be updated within an hour.<br><br>
                      <strong>2. Topic Categorization</strong><br>
                      Billing<br><br>
                      <strong>3. Sentiment Analysis</strong><br>
                      Overall, the call had a positive sentiment. While the customer was initially concerned about the incorrect billing and interest charge, she was pleased with the agent's assistance in resolving the issue promptly.<br><br>
                      <strong>4. Issue Identification, Resolution, and Recommendations:</strong><br>
                      <strong>Issue Identification:</strong><br>
                      Incorrect billing amount due to missed automated withdrawals<br>
                      Interest charge on unpaid balance<br>
                      <strong>Resolution:</strong><br>
                      Interest charge was removed<br>
                      Automated withdrawals were reactivated<br>
                      Revised balance was updated on the customer's online account within an hour<br>
                      <strong>Recommendation:</strong><br>
                      Set up a system to notify customers when their automated withdrawal is turned off after a certain number of years<br><br>
                      <strong>5. Agent Performance:</strong><br>
                      The agent's performance was excellent. He/she handled the customer's concerns promptly, and resolved the issue efficiently. The agent was also polite and helpful, and provided the customer with clear instructions on how to reactivate her automated withdrawals.
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="success" class="mt-6">
                      The call summary was accurate. The call was successfully classified as a billing inquiry. The sentiment of the call was positive, and the issue was resolved to the customer's satisfaction. The customer service representative demonstrated good performance and professionalism.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>Google: Chirp v2 + Gemini Pro</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription</h3>
                    <ul>
                      <li>hi this is richard from hydroadowa how can help you hi richard this is diane i received a a bill my bills are online my uh invoices and um the last one i just received there was a large amount and i wondered why and then noticed that our automated withdraw stopped all of a sudden uh last november i see okay uh and we didn't do anything to stop</li>
                      <li>that we had moved in july and we had called and got the address changed and done all that so um now we've been charged some interest and i'd like to have that looked at and interest removed so we can just pay what we're owed owing what's owing okay uh let's see here um can i see the uh what's the address it's um 3478 greens road</li>
                      <li>is like g r e e n s apostrophe s okay and is it going yes okay and can i verify uh the data of birth september 3rd 1960 okay and can i verify the phone number on the file 613 990715</li>
                      <li>that might be one of them yeah i see there's a was last payment we received was way back in october it looks like it was three bills all together but the interest honestly it wasn't that much it was only $1.52 uh yeah i know but i mean the fact that we didn't make this change i don't really feel we should pay any interest i just like to get it rectified and i could take the i could take the dollar 52 off it's fine but uh yeah okay that's for you</li>
                      <li>can you see why the um automated withdraw was turned off cuz we've had this for like 20 years pluss okay okay</li>
                      <li>so looks like um all right yeah there you go i cancel the interest that's fine so now the remaining balance is $254.70 and the reason why the autoop was can't sold yeah since it has um 2007 to 2023 so yeah um so in the system it always it always after so many years it it stops the uh</li>
                      <li>the autopay after so many years okay well there should be some sort of notice or something right when you first sign up for the autoop on the form it says after a while yeah that's a long time i i know that's a long time to know and because it's automated payments you know it and i've kept every um invoice and notice from hydro autowalk nothing like that just a suggestion it might be good to set up some sort of notice that</li>
                      <li>tells people that yeah yeah okay we'll do that okay so yeah the runing balance it's uh now $254.70 okay okay so can you automatically withdraw that or do i have to make a payment oh you have to make payment manually for this one yeah uh-huh figured that okay all right so can you reactivate the automated withdraw for the next bill okay so you'd have to</li>
                      <li>do that um i can't activate it the customer has to do that um okay i'm online so where do i go to do that let's see here one second i'm not happy about that okay no never mind okay i did it myself here okay i just extended the date now to now so now it ends in 2027 okay</li>
                      <li>okay yeah there you go okay now it's active again but then this yeah but this has to be pit manually okay so when will i see the revised amount so that i can pay the bill buse i just refreshed my site and it's not uh you should see it on there within give it like an hour or so to update in the system okay okay okay okay thanks very much no worries you have yourself a good one okay okay you too</li>
                    </ul>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (Gemini Pro)</h3>
                      <strong>1. Summary of the Call</strong><br>
                      A customer named Diane called Hydro Ottawa to inquire about a large amount on her recent bill and interest charges. She explained that her automated withdrawals for bill payments had stopped without her knowledge and wanted the interest charges removed. The agent cancelled the interest charges and explained that the automated withdrawals had stopped because the feature had expired after many years. The agent suggested Diane reactivate the automated withdrawals, which she did herself.<br><br>
                      <strong>2. Topic Categorization</strong><br>
                      Billing<br><br>
                      <strong>3. Sentiment Analysis</strong><br>
                      The customer's sentiment was initially negative due to the unexpected interest charges and the inconvenience of having to manually make a payment. However, the agent's prompt and helpful response improved her mood, and she expressed satisfaction with the resolution.<br><br>
                      <strong>4. Issue Identification, Resolution, and Recommendation:</strong><br>
                      <strong>Issue:</strong><br>
                      Interest charges on the bill and automated withdrawals had stopped.<br>
                      <strong>Resolution:</strong><br>
                      Interest charges were cancelled.<br>
                      Automated withdrawals were reactivated.<br>
                      <strong>Recommendation:</strong><br>
                      Hydro Ottawa should implement a system to notify customers when automated withdrawals are about to expire.<br><br>
                      <strong>5. Agent Performance:</strong><br>
                      The agent was polite, empathetic, and efficient. They quickly identified the issue, resolved it promptly, and provided clear instructions to the customer. The agent also provided additional information and suggestions to improve the customer's experience.
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="success" class="mt-6">
                      The call summary was accurate. The call was successfully classified as a billing inquiry. The sentiment of the call was positive, and the issue was resolved to the customer's satisfaction. The customer service representative demonstrated good performance and professionalism.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>Hybrid: Whisper + Gemini Pro</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription (OpenAI Whisper)</h3>
                    <p>
                      Hi, this is Richard from Hydro Ottawa. How can I help you? Hi Richard, this is Diane. I received a bill. My bills are online, my invoices. And the last one I just received, there was a large amount and I wondered why. And then noticed that our automated withdrawal stopped all of a sudden last November. I see, okay. And we didn't do anything to stop that. We had moved in July and we had called and got the address changed and done all that. So now we've been charged some interest and I'd like to have that looked at and the interest removed so we can just pay what we're owing. Okay, let's see here. Can I see the address? It's 3478 Greens Road. Is that G-R-E-N-S? Apostrophe S. Okay. And is it Diane? Yes. Okay, and can I verify the date of birth? September 3, 1960. Okay, and can I verify the phone number on the file? 613-990-7715. That might be one of them. Okay. Yeah, I see there was a last payment received was way back in October and it looks like there was three bills altogether. But the interest, honestly, wasn't that much. It was only $1.52. Yeah, I know, but I mean the fact that we didn't make this change, I don't really feel we should pay any interest. I'd like to get it rectified. I could take the $1.52 off. It's fine. Yeah, okay, great. Can you see why the automated withdrawal was turned off? Because we've had this for like 20 years plus. Okay, one second. Okay, so it looks like, oh, yeah, there you go. I canceled the interest. That's fine. Okay. So now the remaining balance is $254.70. The reason why the auto pay was canceled, yeah, since it has, so it was from 2007 until 2023. So, yeah, so in the system it always, after so many years, it stops the auto pay after so many years. Okay, well, there should be some sort of notice or something, right? When you first sign up for the auto pay on the form, it says that after a while you have to be. That's a long time. I know. That's a long time to know. And because it's automated payments, you know, and I've kept every invoice and notice from Hydro Ottawa, nothing like that. Just a suggestion, it might be good to set up some sort of notice that tells people that. Yeah, yeah. Okay, I think we'll do that. Okay, so, yeah, the remaining balance now is only $254.70. Okay. Okay, so can you automatically withdraw that or do I have to make a payment? Oh, you have to make a payment manually for this one, yes. Uh-huh, figured that. Okay. All right, so can you reactivate the automated withdrawal for the next bill? Okay, so you'd have to do that. I can't activate it. The customer has to do that. Okay, I'm online, so where do I go to do that? Let me see here one second. I'm not happy about that. Okay, no, never mind. Okay, I did it myself here. Okay, I just extended the date now to, so now it ends in 2027. Okay. Okay, yeah, there you go. Okay, now it's active again. Yeah, but this has to be paid manually. Okay, so when will I see the revised amount so that I can pay the bill? Because I just refreshed my site and it's not. You should see it on there within, give it like an hour or so to update the system. Okay. Okay. Okay. Okay, thanks very much. No worries. You have yourself a good one. Okay. Okay, you too. Bye now. Bye now.
                    </p>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (Gemini Pro)</h3>
                      <strong>1. Detailed Summary of the Call</strong><br>
                      The customer, Diane, called Hydro Ottawa to inquire about a large amount on her recent bill and to have interest charges removed. She explained that her automated withdrawal had stopped in November without her knowledge and that she had moved in July and had updated her address. The agent identified that the auto pay had stopped due to inactivity after a certain number of years and canceled the interest charges on the account. The agent also advised the customer that she would need to make a manual payment for the current bill and reactivate the automated withdrawal herself through her online account.<br><br>
                      <strong>2. Topic Categorization</strong><br>
                      Billing<br><br>
                      <strong>3. Sentiment Analysis:</strong><br>
                      Overall, the sentiment of the call is positive. The customer was initially concerned about the interest charges but was satisfied with the agent's resolution. However, she expressed some dissatisfaction with having to reactivate the automated withdrawal herself and the lack of a notification regarding the auto pay deactivation.<br><br>
                      <strong>4. Issue Identification, Resolution and Recommendation</strong><br>
                      <strong>Issue:</strong><br>
                      Automated withdrawal stopped without customer's knowledge<br>
                      Interest charges applied<br>
                      <strong>Resolution:</strong><br>
                      Interest charges canceled<br>
                      Customer advised to reactivate automated withdrawal manually<br>
                      <strong>Recommendation:</strong><br>
                      Hydro Ottawa could consider implementing a notification system to inform customers when their auto pay is about to be deactivated due to inactivity.<br><br>
                      <strong>5. Agent Performance</strong><br>
                      The agent demonstrated good customer service skills. They were:<br>
                      Empathetic: Understood the customer's concerns and acknowledged her frustration.<br>
                      Knowledgeable: Provided accurate and timely information about the issue and resolution.<br>
                      Problem-solving: Canceled the interest charges and provided solutions to the customer's concerns.<br>
                      Responsive: Addressed the customer's questions and requests promptly.
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="success" class="mt-6">
                      The call summary was accurate. The call was successfully classified as a billing inquiry. The sentiment of the call was positive, and the issue was resolved to the customer's satisfaction. The customer service representative demonstrated good performance and professionalism.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>OpenAI: Whisper + ChatGPT 4</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription (Whisper)</h3>
                    <p>
                      Hi, this is Richard from Hydro Ottawa. How can I help you? Hi Richard, this is Diane. I received a bill. My bills are online, my invoices. And the last one I just received, there was a large amount and I wondered why. And then noticed that our automated withdrawal stopped all of a sudden last November. I see, okay. And we didn't do anything to stop that. We had moved in July and we had called and got the address changed and done all that. So now we've been charged some interest and I'd like to have that looked at and the interest removed so we can just pay what we're owing. Okay, let's see here. Can I see the address? It's 3478 Greens Road. Is that G-R-E-N-S? Apostrophe S. Okay. And is it Diane? Yes. Okay, and can I verify the date of birth? September 3, 1960. Okay, and can I verify the phone number on the file? 613-990-7715. That might be one of them. Okay. Yeah, I see there was a last payment received was way back in October and it looks like there was three bills altogether. But the interest, honestly, wasn't that much. It was only $1.52. Yeah, I know, but I mean the fact that we didn't make this change, I don't really feel we should pay any interest. I'd like to get it rectified. I could take the $1.52 off. It's fine. Yeah, okay, great. Can you see why the automated withdrawal was turned off? Because we've had this for like 20 years plus. Okay, one second. Okay, so it looks like, oh, yeah, there you go. I canceled the interest. That's fine. Okay. So now the remaining balance is $254.70. The reason why the auto pay was canceled, yeah, since it has, so it was from 2007 until 2023. So, yeah, so in the system it always, after so many years, it stops the auto pay after so many years. Okay, well, there should be some sort of notice or something, right? When you first sign up for the auto pay on the form, it says that after a while you have to be. That's a long time. I know. That's a long time to know. And because it's automated payments, you know, and I've kept every invoice and notice from Hydro Ottawa, nothing like that. Just a suggestion, it might be good to set up some sort of notice that tells people that. Yeah, yeah. Okay, I think we'll do that. Okay, so, yeah, the remaining balance now is only $254.70. Okay. Okay, so can you automatically withdraw that or do I have to make a payment? Oh, you have to make a payment manually for this one, yes. Uh-huh, figured that. Okay. All right, so can you reactivate the automated withdrawal for the next bill? Okay, so you'd have to do that. I can't activate it. The customer has to do that. Okay, I'm online, so where do I go to do that? Let me see here one second. I'm not happy about that. Okay, no, never mind. Okay, I did it myself here. Okay, I just extended the date now to, so now it ends in 2027. Okay. Okay, yeah, there you go. Okay, now it's active again. Yeah, but this has to be paid manually. Okay, so when will I see the revised amount so that I can pay the bill? Because I just refreshed my site and it's not. You should see it on there within, give it like an hour or so to update the system. Okay. Okay. Okay. Okay, thanks very much. No worries. You have yourself a good one. Okay. Okay, you too. Bye now. Bye now.
                    </p>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (ChatGPT4)</h3>
                      <strong>Detailed Summary</strong><br>
                      Diane called Hydro Ottawa regarding an unexpectedly large amount on her latest bill. She discovered that her automated withdrawal for bill payments, which had been set up for over 20 years, had unexpectedly stopped in November, following their move in July, even though they had updated their address with Hydro Ottawa. This resulted in her being charged interest. Diane requested the interest be waived and the issue with the automated withdrawal to be resolved. The customer service representative, Richard, confirmed her details and found that the interest amounted to $1.52, which he then waived. He explained that the automated withdrawal was stopped because the system cancels auto payments after a certain number of years. Diane expressed dissatisfaction with the lack of notice about this automatic cancellation. Richard reactivated the automated withdrawal, extending it to 2027, but noted that the current outstanding balance would need to be paid manually. He assured Diane that the updated balance would reflect on her account within an hour.<br><br>
                      <strong>Topic Categorization</strong><br>
                      Billing<br><br>
                      Also, the call could be categorized under the following topics:<br>
                      <ul>
                        <li>Billing: The call primarily concerns a billing issue related to unexpected charges and interest.</li>
                        <li>Account Maintenance: Diane's call involves updating her billing preferences and resolving issues related to her account following a move.</li>
                        <li>Pre-Authorized Payment Plan: The issue also pertains to the automated withdrawal payment plan, its unexpected cancellation, and reactivation.</li>
                        <li>Complaint & Escalations: Diane expressed dissatisfaction with the lack of notice about the automatic cancellation of her payment plan, which could be viewed as a complaint.</li>
                      </ul>
                      <br><br>
                      <strong>Sentiment Analysis</strong><br>
                      Customer Sentiment: Initially, Diane's sentiment was one of confusion and slight frustration due to the unexpected charge and the stoppage of the automated payment. However, her tone seemed to become more positive after Richard resolved the interest charge issue and reactivated the auto-payment, despite the inconvenience of having to make a manual payment for the current bill.<br>
                      Agent Sentiment: Richard maintained a helpful and accommodating demeanor throughout the call, promptly addressing Diane's concerns, offering solutions, and ultimately resolving the issues raised.<br><br>
                      <strong>Issue Identification, Resolution, and Recommendation</strong><br>
                      <strong>Issue Identification:</strong> The primary issue was the unexpected cessation of the automated withdrawal for bill payments, leading to an interest charge.<br>
                      <strong>Resolution:</strong> Richard waived the interest charge and reactivated the automated withdrawal for future payments.<br>
                      <strong>Recommendation:</strong> It would be beneficial for Hydro Ottawa to implement a notification system to alert customers before the automatic cancellation of their payment plans. This would prevent similar issues and improve customer satisfaction.<br><br>
                      <strong>Agent Performance</strong><br>
                      Richard demonstrated effective customer service by quickly understanding Diane's issues, verifying her account details, and providing immediate solutions. He successfully resolved the interest charge and reactivated the automated payment plan, meeting the customer's needs. However, there is room for improvement in preventing such issues from arising, such as better communication about policy regarding automatic payment plan cancellations. His performance was efficient and solution-oriented, which likely contributed to a positive customer experience for Diane.
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="success" class="mt-6">
                      This combination of Whisper and ChatGPT4 demonstrated the most understanding of the call.
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header>Hybrid: Whisper + Claude 3</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3>Transcription (OpenAI Whisper)</h3>
                    <p>
                      Hi, this is Richard from Hydro Ottawa. How can I help you? Hi Richard, this is Diane. I received a bill. My bills are online, my invoices. And the last one I just received, there was a large amount and I wondered why. And then noticed that our automated withdrawal stopped all of a sudden last November. I see, okay. And we didn't do anything to stop that. We had moved in July and we had called and got the address changed and done all that. So now we've been charged some interest and I'd like to have that looked at and the interest removed so we can just pay what we're owing. Okay, let's see here. Can I see the address? It's 3478 Greens Road. Is that G-R-E-N-S? Apostrophe S. Okay. And is it Diane? Yes. Okay, and can I verify the date of birth? September 3, 1960. Okay, and can I verify the phone number on the file? 613-990-7715. That might be one of them. Okay. Yeah, I see there was a last payment received was way back in October and it looks like there was three bills altogether. But the interest, honestly, wasn't that much. It was only $1.52. Yeah, I know, but I mean the fact that we didn't make this change, I don't really feel we should pay any interest. I'd like to get it rectified. I could take the $1.52 off. It's fine. Yeah, okay, great. Can you see why the automated withdrawal was turned off? Because we've had this for like 20 years plus. Okay, one second. Okay, so it looks like, oh, yeah, there you go. I canceled the interest. That's fine. Okay. So now the remaining balance is $254.70. The reason why the auto pay was canceled, yeah, since it has, so it was from 2007 until 2023. So, yeah, so in the system it always, after so many years, it stops the auto pay after so many years. Okay, well, there should be some sort of notice or something, right? When you first sign up for the auto pay on the form, it says that after a while you have to be. That's a long time. I know. That's a long time to know. And because it's automated payments, you know, and I've kept every invoice and notice from Hydro Ottawa, nothing like that. Just a suggestion, it might be good to set up some sort of notice that tells people that. Yeah, yeah. Okay, I think we'll do that. Okay, so, yeah, the remaining balance now is only $254.70. Okay. Okay, so can you automatically withdraw that or do I have to make a payment? Oh, you have to make a payment manually for this one, yes. Uh-huh, figured that. Okay. All right, so can you reactivate the automated withdrawal for the next bill? Okay, so you'd have to do that. I can't activate it. The customer has to do that. Okay, I'm online, so where do I go to do that? Let me see here one second. I'm not happy about that. Okay, no, never mind. Okay, I did it myself here. Okay, I just extended the date now to, so now it ends in 2027. Okay. Okay, yeah, there you go. Okay, now it's active again. Yeah, but this has to be paid manually. Okay, so when will I see the revised amount so that I can pay the bill? Because I just refreshed my site and it's not. You should see it on there within, give it like an hour or so to update the system. Okay. Okay. Okay. Okay, thanks very much. No worries. You have yourself a good one. Okay. Okay, you too. Bye now. Bye now.
                    </p>
                    <v-sheet color="grey lighten-4 mt-6 pa-6">
                      <h3 class="mb-6">Classification (Claude 3)</h3>
                      <strong>1. Detailed Summary of the Call</strong><br>
                      The customer, Diane, received a higher bill and noticed her automated bill payment had stopped in November after moving in July. She called to get the interest charges removed and understand why the automated payment was cancelled. The agent, Richard, verified her information, removed the $1.52 interest charge, and explained their policy of cancelling autopay after many years, though Diane was unaware of this policy. Richard acknowledged the need for better notification. The remaining balance was $254.70, to be paid manually. Diane reactivated autopay online herself, extending it to 2027. The revised bill amount would update within an hour.
                      <strong>2. Topic Categorization</strong><br>
                      Billing, Automated Payment<br><br>
                      <strong>3. Sentiment Analysis:</strong><br>
                      Diane seemed frustrated initially about the unexpected charges and lack of notification. However, her sentiment improved as Richard resolved the issue professionally.<br><br>
                      <strong>4. Issue Identification, Resolution and Recommendation</strong><br>
                      <strong>Issue:</strong><br>
                      <ol>
                        <li>Higher than expected bill</li>
                        <li>Automated payment unexpectedly cancelled</li>
                        <li>Interest charges due to missed payments</li>
                      </ol><br><br>
                      <strong>Resolution:</strong><br>
                      <ol>
                        <li>Interest charge of $1.52 removed</li>
                        <li>Explained autopay cancellation policy, though better notification needed</li>
                        <li>$254.70 balance to be paid manually</li>
                        <li>Customer reactivated autopay online until 2027</li>
                      </ol><br><br>
                      <strong>Recommendation:</strong><br>
                      Implement a notification system to inform customers when autopay will be cancelled per policy, well in advance.<br><br>
                      <strong>5. Agent Performance</strong><br>
                      Richard handled the call professionally, providing clear explanations and resolving concerns. However, he could have offered to reactivate autopay for the customer.<br><br>
                    </v-sheet>
                    <h3 class="mt-6">Result</h3>
                    <v-alert type="warning" class="mt-6">
                      The call summary was mostly accurate
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-tab-item>

            <!-- ------------ Proof of Concept ------------ -->
            <v-tab-item class="pa-6">
              <h2 class="mb-6">Proof of Concept</h2>
              <v-row class="pa-2">
                <v-col v-for="(usecase, index) in usecases" :key="usecase.id" cols="12" xs="12" sm="6" md="3">
                  <v-card class="mx-auto" style="height: 100%">
                    <v-img
                      class="white--text align-end"
                      height="200px"
                      :src="usecase.image"
                      @click="showDialog(index)"
                    >
                    </v-img>
                    <v-divider></v-divider>
                    <v-card-title>
                      {{ usecase.name }}
                    </v-card-title>
                    <v-card-subtitle>
                      {{ usecase.id }}
                    </v-card-subtitle>
                    <v-card-text class="text--primary">
                      {{ usecase.description }}
                    </v-card-text>
                    <!-- <v-divider></v-divider>
                    <v-card-actions>
                      <audio ref="player" :src="usecase.recording" controls></audio>
                      <v-spacer></v-spacer>
                      <v-btn color="orange" text @click="showDialog(index)">View</v-btn>
                    </v-card-actions> -->
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- ------------ Pipeline ------------ -->
            <v-tab-item class="pa-6">
              <h2 class="mb-6">Pipeline</h2>
              <v-timeline dense>
                <v-timeline-item v-for="(step, i) in pipeline" :key="i" :color="step.colour" small :icon="step.icon">
                  <template>
                    <span
                      :class="`headline font-weight-bold ${step.text_colour}`"
                      v-text="step.title"
                    ></span>
                  </template>
                  <div class="py-4">
                    <h2 :class="`headline font-weight-light mb-4 ${step.text_colour}`">
                      {{ step.subtitle }}
                    </h2>
                    <div>
                      {{ step.description }}
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-tab-item>

            <!-- ------------ Dataset ------------ -->
            <v-tab-item class="pa-6">
              <h2 class="mb-6">Dataset</h2>
              <v-progress-linear
                v-if="loadingDataset"
                indeterminate
                color="primary"
                class="mb-6">
              </v-progress-linear>

              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">ID</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Transcription</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in items" :key="item.id" @click="viewTranscript(item.id, item.transcription.chirp)">
                      <td>{{ item.id }}</td>
                      <td>{{ item.date }}</td>
                      <td>{{ item.transcription.chirp | truncate }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- <button @click="prevPage" :disabled="currentPage === 1">Previous</button> -->
              <!-- <span>Page {{ currentPage }} of {{ totalPages }}</span> -->
              <button @click="nextPage" :disabled="currentPage === totalPages">Load More</button>
              
            </v-tab-item>
          </v-tabs-items>
      
      <v-dialog v-model="callDialog" width="800">
        <v-card class="text-left">
          <v-card-title>
            {{ selectedItem?.id }}
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="callDialog = false" icon><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-sheet color="#f1f3f4" class="d-flex align-center">
            <audio ref="transcriptPlayer" :src="'https://storage.googleapis.com/hydroottawa-audio/converted/converted_' + selectedItem?.id + '.wav'" controls></audio>
          </v-sheet>
          <v-card-text class="mt-6">
            <v-row>
              <v-col cols="12" md="6">
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Summary</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>{{ selectedItem?.reasoning?.gemini?.Summary?.detailed }}</v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Transcription (Google)</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>{{ selectedItem?.transcription?.chirp }}</v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Transcription (OpenAI)</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>{{ selectedItem?.transcription?.whisper }}</v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
              <v-col cols="12" md="6">
                <v-expansion-panels>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Categories</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>CC&B Category:</strong> {{ selectedItem?.reasoning?.gemini?.Categorization?.category }}<br>
                      <strong>CC&B Category Reasoning:</strong> {{ selectedItem?.reasoning?.gemini?.Categorization?.reasoning }}<br>
                      <hr>
                      <strong>Primary:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.primary_issue }}<br>
                      <strong>Secondary:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.secondary_issues | joinArray }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Call Information</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Date:</strong> {{ selectedItem?.date }}<br>
                      <strong>Quality:</strong> {{ selectedItem?.reasoning?.gemini?.['Call Information']?.call_quality }}<br>
                      <strong>Customer:</strong> {{ selectedItem?.reasoning?.gemini?.['Call Information']?.caller_name }}<br>
                      <strong>Address:</strong> {{ selectedItem?.reasoning?.gemini?.['Call Information']?.caller_address }}<br>
                      <strong>Agent:</strong> {{ selectedItem?.reasoning?.gemini?.['Call Information']?.agent_name }}<br>
                      <strong>Language:</strong> {{ selectedItem?.reasoning?.gemini?.['Language']?.language }}<br>
                      <strong>Curses:</strong> {{ selectedItem?.reasoning?.gemini?.['Language']?.curses }}<br>
                      <strong>Hold Occurrences:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.hold_occurrences }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Issue Identification</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Customer Concerns:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.issue_identification?.customer_concerns }}<br>
                      <strong>Specific Concerns:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.issue_identification?.specific_issues | joinArray }}<br>
                      <strong>Agreed Solution:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.resolution?.agreed_solution }}<br>
                      <strong>Confirmation Number:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.resolution?.confirmation_number }}<br>
                      <strong>Recommendations:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.resolution?.recommendations | joinArray }}<br>
                      <strong>Follow-up:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.resolution?.follow_up }}<br>
                      <strong>Next Steps:</strong> {{ selectedItem?.reasoning?.gemini?.['Topic Categorization']?.resolution?.next_steps | joinArray }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">MyAccount</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Expired Password?:</strong> {{ selectedItem?.reasoning?.gemini?.['MyAccount']?.expired_password }}<br>
                      <strong>Issues:</strong> {{ selectedItem?.reasoning?.gemini?.['MyAccount']?.myaccount_issues | joinArray }}<br>
                      <strong>Tried MyAccount:</strong> {{ selectedItem?.reasoning?.gemini?.['MyAccount']?.tried_myaccount }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Sentiment</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Agent:</strong> {{ selectedItem?.reasoning?.gemini?.['Sentiment']?.agent }}<br>
                      <strong>Customer:</strong> {{ selectedItem?.reasoning?.gemini?.['Sentiment']?.customer }}<br>
                      <strong>Overall:</strong> {{ selectedItem?.reasoning?.gemini?.['Sentiment']?.overall }}<br>
                      <strong>Score:</strong> {{ selectedItem?.reasoning?.gemini?.['Sentiment']?.score }}<br>
                      <strong>Shifts?:</strong> {{ selectedItem?.reasoning?.gemini?.['Sentiment']?.shifts | joinArray }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Policy Training</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Knowledge:</strong> {{ selectedItem?.reasoning?.gemini?.['AI Training']?.valuable_information | joinArray }}<br>
                      <strong>Policies:</strong> {{ selectedItem?.reasoning?.gemini?.['AI Training']?.policies | joinArray }}<br>
                      <strong>Procedures:</strong> {{ selectedItem?.reasoning?.gemini?.['AI Training']?.procedures | joinArray }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Triage Training</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Customer:</strong> {{ selectedItem?.reasoning?.gemini?.['Triage']?.first_customer_statement }}<br>
                      <strong>Human Response:</strong> {{ selectedItem?.reasoning?.gemini?.['Triage']?.first_agent_response }}<br>
                      <strong>AI Response:</strong> {{ selectedItem?.reasoning?.gemini?.['Triage']?.ai_response }}<br>
                      <strong>Scenario:</strong> {{ selectedItem?.reasoning?.gemini?.['Agent Performance and Training']?.training?.scenario }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Key Actions</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Programs & Services Discussed:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.programs_and_services_discussed | joinArray }}<br>
                      <strong>Agent Guidance:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.agent_guidance | joinArray }}<br>
                      <strong>Documentation:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.documentation_needs }}<br>
                      <strong>Payment:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.payment_information }}<br>
                      <strong>Modifications:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.cancellation_modification_actions | joinArray }}<br>
                      <strong>Payment Plan Changed:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.payment_plan_setup }}<br>
                      <strong>System References:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.system_references | joinArray }}<br>
                      <strong>Verification Process:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.verification_process | joinArray }}<br>
                      <strong>Agent Follow-up Confirmation:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.agent_followup_confirmation }}<br>
                      <strong>Customer Requests:</strong> {{ selectedItem?.reasoning?.gemini?.['Key Actions']?.customer_requests | joinArray }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Agent Performance and Training</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>Best Practices:</strong> {{ selectedItem?.reasoning?.gemini?.['Agent Performance and Training']?.best_practices | joinArray }}<br>
                      <strong>Improvement Areas:</strong> {{ selectedItem?.reasoning?.gemini?.['Agent Performance and Training']?.improvement_areas | joinArray }}<br>
                      <strong>Best Practices:</strong> {{ selectedItem?.reasoning?.gemini?.['Agent Performance and Training']?.training?.scenario }}<br>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-h6">Improvements</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <strong>System Improvements:</strong> {{ improvements(selectedItem?.reasoning?.gemini?.['Improvements']?.system_improvements) }}<br>
                      <strong>Service Improvements:</strong> {{ improvements(selectedItem?.reasoning?.gemini?.['Improvements']?.service_improvements) }}<br>
                      <!-- <strong>Call Prevention:</strong> {{ selectedItem?.reasoning?.gemini?.['Improvements']?.system_improvements | joinArray }}<br> -->
                    </v-expansion-panel-content>
                  </v-expansion-panel>
  
                </v-expansion-panels>
  
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-if="selectedUseCase" v-model="dialog" width="800">
        <v-card class="text-left">
          <div class="d-flex flex-row">
            <div class="d-flex flex-column flex-2 pa-6">
              <v-card-title>
                {{ selectedUseCase.id }}
              </v-card-title>
              <v-card-text>
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>Transcript</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      {{ selectedUseCase.transcription }}
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <div class="mt-8 mb-6">
                  <h3 class="mb-6">Detailed Summary</h3>
                  {{ selectedUseCase.understanding.summary }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Topic Categorization</h3>
                  {{ selectedUseCase.understanding.category }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Sentiment</h3>
                  {{ selectedUseCase.understanding.sentiment }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Issue Identification</h3>
                  {{ selectedUseCase.understanding.issue }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Resolution</h3>
                  {{ selectedUseCase.understanding.resolution }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Recommendation</h3>
                  {{ selectedUseCase.understanding.recommendation }}
                </div>
                <div class="mb-6">
                  <h3 class="mb-6">Agent Performance</h3>
                  {{ selectedUseCase.understanding.performance }}
                </div>
              </v-card-text>
              <v-card-actions>

              </v-card-actions>
            </div>
            <div class="d-flex flex-column flex-1 pa-6 white--text" style="background: black">
              <h4 class="mb-6">Call Recording</h4>
              <audio ref="player" :src="selectedUseCase.recording" controls></audio>
              <!-- <v-card class="pa-3 pb-1 mt-3">
                <v-slider hide-details></v-slider>
                <div class="d-flex flex-row">
                  <v-spacer></v-spacer>
                  <v-btn icon @click="playRecording()"><v-icon>mdi-play</v-icon></v-btn>
                  <v-btn icon @click="stopRecording()"><v-icon>mdi-stop</v-icon></v-btn>
                </div>
              </v-card> -->
            </div>
          </div>
        </v-card>
      </v-dialog>
      <v-dialog v-model="transcriptionDialog" width="800">
        <v-card class="text-left">
          <v-card-title>
            {{ transcriptionTitle }}
          </v-card-title>
          <v-sheet color="#f1f3f4" class="d-flex align-center">
            <audio ref="transcriptPlayer" :src="'https://storage.googleapis.com/hydroottawa-audio/converted/converted_' + transcriptionTitle + '.wav'" controls></audio>
          </v-sheet>
          <v-card-text class="mt-6">
            {{ transcriptionText }}
          </v-card-text>
        </v-card>
      </v-dialog>
  </v-card-text>
</template>
  
<script>
  import localCategories from '@/assets/categories_v2.json';
  import { getFirestore, collection, getDoc, getDocs, query, limit, startAfter, addDoc, setDoc, doc } from 'firebase/firestore'
  
  export default ({
    name: 'CallAnalysisConcept',
    props: ['user'],
    data: () => ({
      // General
      tab: 0,
      // Data Explorer
      explorerLoading: false,
      progress: 0,
      items: [],
      filteredItems: [],
      itemsPerPage: 10,
      page: 1,
      pageCount: 0,
      processed: 0,
      matches: 0,
      search: '',
      headers: [
        { text: 'Call ID', value: 'id' },
        { text: 'Date', value: 'date' },
        { text: 'Category', value: 'category' },
        // { text: 'Sentiment', value: 'sentiment' },
        { text: 'Length', value: 'length' },
        { text: 'Summary', value: 'summary' },
      ],
      panel: 0,
      //
      callDialog: false,
      selectedItem: null,
      stopLoadingFlag: false,
      lastVisible: null,  // Add a variable to store the last visible document
      timer: 0, // Timer for loading processes
      timerInterval: null, // Interval for the timer
      // Assistant
      assistantLoading: false,
      userQuery: '',
      sessionId: Date.now().toString(36) + Math.random().toString(36), // Generate a simple unique session ID
      prompts: [
        { title: 'Analyze the call quality', class: 'primary' },
        { title: 'How can Hydro Ottawa improve', class: 'primary' },
        { title: 'what are customers calling about the most, list one thing', class: 'primary' },
        { title: 'can you breakdown the billing issues into 5 sub categories', class: 'primary' },
        { title: 'were there any calls that stood out as being a non-standard category', class: 'primary' },
        { title: 'tell me more about the power quality issues', class: 'primary' },
        { title: 'draft an email explaining that to a customer that has complained about this', class: 'primary' },
      ],
      history: [],
      explorerLogDocId: null, // Separate logDocId for Explorer
      assistantLogDocId: null, // Separate logDocId for Assistant
  
      // Categories
      categories: [],
      // Journeys
      journeys: [],
      // Loading States
      explorerLoadingState: 0,
      journeysLoadingState: 0,
      categoriesLoadingState: 0,

      //
      dialog: false,
      player: null,
      usecaseDetails: false,
      //
      loadingDataset: true,
      currentPage: 1,
      totalPages: null,
      itemsPerPage: 100, // Adjust the limit as needed
      lastVisible: null,
      items: [],
      // 
      transcriptionDialog: false,
      transcriptionTitle: '',
      transcriptionText: '',
      //
      categoryString: 'Billing, Moves, Not Coded by Agent, Account Maintenance, Collections, Payment, MyAccount Operations, Other, Reconnections, Pre-Authorized Payment Plan, Social Assist, Forestry, LLR, Outages, Booking, Battery Loan Inquiry, Rate Option, Document Request, Surveys, Mobile Application, Layouts, Outage Alert Inquiry, Scam, Usage Inquiry, Covid Energy Assistance Program, Next Level Complaint, Deposit Inquiry, Complaint & Escalations, Cancelling, EV Inquiry, LD, Promotion, IVR Self-Service, Usage Online, Home Energy Reports, CDM, Green Button, Supervisions',
      categories: [
        'Billing', 
        'Moves',
        'Not Coded by Agent',
        'Acct Maintenance',
        'Collections',
        'Payment',
        'MyAccount Operations',
        'Other',
        'Reconnections',
        'Pre-Authorized Payment Plan',
        'Social Assist',
        'Forestry',
        'LLR',
        'Outages',
        'Booking',
        'Battery Loan Inquiry',
        'Rate Option',
        'Document Request',
        'Surveys',
        'MOBILE APP',
        'Layouts',
        'Outage Alert Inq',
        'Scam',
        'Usage Inq',
        'CEAPSB',
        'Next Level Complaint',
        'Deposit Inquiry',
        'Complaint & Escalations',
        'Cancelling',
        'EV Inquiry',
        'LD',
        'PROMOTION',
        'IVR Self-Service',
        'Usage Online',
        'CEAP',
        'Home Energy Reports',
        'CDM',
        'Green Button',
        'Supervisions',
      ],
      useCaseDialog: false,
      selectedUseCase: null,
      usecases: [
        {
          id: 'F260115181857V1U2300033',
          name: 'Diane',
          image: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2Fdiane.webp?alt=media&token=f6156d41-8463-41b6-a3e9-b6c36e576db1',
          recording: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2FF260115181857V1U2300033.mp3?alt=media&token=1a69ee1f-0d33-4c71-8769-355e123dfcc5',
          description: 'The primary issue was the unexpected cessation of the automated withdrawal for bill payments, leading to an interest charge.',
          transcription: "Hi, this is Richard from Hydro Ottawa. How can I help you? Hi Richard, this is Diane. I received a bill. My bills are online, my invoices. And the last one I just received, there was a large amount and I wondered why. And then noticed that our automated withdrawal stopped all of a sudden last November. I see, okay. And we didn't do anything to stop that. We had moved in July and we had called and got the address changed and done all that. So now we've been charged some interest and I'd like to have that looked at and the interest removed so we can just pay what we're owing. Okay, let's see here. Can I see the address? It's 3478 Greens Road. Is that G-R-E-N-S? Apostrophe S. Okay. And is it Diane? Yes. Okay, and can I verify the date of birth? September 3, 1960. Okay, and can I verify the phone number on the file? 613-990-7715. That might be one of them. Okay. Yeah, I see there was a last payment received was way back in October and it looks like there was three bills altogether. But the interest, honestly, wasn't that much. It was only $1.52. Yeah, I know, but I mean the fact that we didn't make this change, I don't really feel we should pay any interest. I'd like to get it rectified. I could take the $1.52 off. It's fine. Yeah, okay, great. Can you see why the automated withdrawal was turned off? Because we've had this for like 20 years plus. Okay, one second. Okay, so it looks like, oh, yeah, there you go. I canceled the interest. That's fine. Okay. So now the remaining balance is $254.70. The reason why the auto pay was canceled, yeah, since it has, so it was from 2007 until 2023. So, yeah, so in the system it always, after so many years, it stops the auto pay after so many years. Okay, well, there should be some sort of notice or something, right? When you first sign up for the auto pay on the form, it says that after a while you have to be. That's a long time. I know. That's a long time to know. And because it's automated payments, you know, and I've kept every invoice and notice from Hydro Ottawa, nothing like that. Just a suggestion, it might be good to set up some sort of notice that tells people that. Yeah, yeah. Okay, I think we'll do that. Okay, so, yeah, the remaining balance now is only $254.70. Okay. Okay, so can you automatically withdraw that or do I have to make a payment? Oh, you have to make a payment manually for this one, yes. Uh-huh, figured that. Okay. All right, so can you reactivate the automated withdrawal for the next bill? Okay, so you'd have to do that. I can't activate it. The customer has to do that. Okay, I'm online, so where do I go to do that? Let me see here one second. I'm not happy about that. Okay, no, never mind. Okay, I did it myself here. Okay, I just extended the date now to, so now it ends in 2027. Okay. Okay, yeah, there you go. Okay, now it's active again. Yeah, but this has to be paid manually. Okay, so when will I see the revised amount so that I can pay the bill? Because I just refreshed my site and it's not. You should see it on there within, give it like an hour or so to update the system. Okay. Okay. Okay. Okay, thanks very much. No worries. You have yourself a good one. Okay. Okay, you too. Bye now. Bye now.",
          understanding: {
            summary: "Diane called Hydro Ottawa regarding an unexpectedly large amount on her latest bill. She discovered that her automated withdrawal for bill payments, which had been set up for over 20 years, had unexpectedly stopped in November, following their move in July, even though they had updated their address with Hydro Ottawa. This resulted in her being charged interest. Diane requested the interest be waived and the issue with the automated withdrawal to be resolved. The customer service representative, Richard, confirmed her details and found that the interest amounted to $1.52, which he then waived. He explained that the automated withdrawal was stopped because the system cancels auto payments after a certain number of years. Diane expressed dissatisfaction with the lack of notice about this automatic cancellation. Richard reactivated the automated withdrawal, extending it to 2027, but noted that the current outstanding balance would need to be paid manually. He assured Diane that the updated balance would reflect on her account within an hour.",
            category: "Billing, Account Maintenance, Pre-Authorized Payment Plan, Complaint & Escalations",
            sentiment: "Customer Sentiment: Initially, Diane's sentiment was one of confusion and slight frustration due to the unexpected charge and the stoppage of the automated payment. However, her tone seemed to become more positive after Richard resolved the interest charge issue and reactivated the auto-payment, despite the inconvenience of having to make a manual payment for the current bill. Agent Sentiment: Richard maintained a helpful and accommodating demeanor throughout the call, promptly addressing Diane's concerns, offering solutions, and ultimately resolving the issues raised.",
            issue: "The primary issue was the unexpected cessation of the automated withdrawal for bill payments, leading to an interest charge.",
            resolution: "Richard waived the interest charge and reactivated the automated withdrawal for future payments.",
            recommendation: "It would be beneficial for Hydro Ottawa to implement a notification system to alert customers before the automatic cancellation of their payment plans. This would prevent similar issues and improve customer satisfaction.",
            performance: "Richard demonstrated effective customer service by quickly understanding Diane's issues, verifying her account details, and providing immediate solutions. He successfully resolved the interest charge and reactivated the automated payment plan, meeting the customer's needs. However, there is room for improvement in preventing such issues from arising, such as better communication about policy regarding automatic payment plan cancellations. His performance was efficient and solution-oriented, which likely contributed to a positive customer experience for Diane.",
            rating: 0
          }
        },
        {
          id: 'F260115153018V1U2300042',
          name: 'Sandra',
          image: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2Fsandra.webp?alt=media&token=88724578-49c6-45fd-8053-53354a5ea8d3',
          recording: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2FF260115153018V1U2300042.mp3?alt=media&token=b6b7800d-a505-4fc5-a61e-786f2f60d783',
          description: 'Sandra had an outstanding balance and made multiple payments to rectify this. She had trouble providing the complete reference number for her last payment due to issues with her banking app.',
          transcription: "Thank you for calling Hydro Ottawa. My name is Maggie. How can I help you today? Hi Maggie. My name is Sandra Hollingshead and I've been a bad girl. But I have managed to straighten that all out in the last ten minutes. So I have a whole batch of information to give you to make certain that I am now current on my account. Okay. Just one second please. Thank you. Alright. Can I please have your name again? Last name first. Certainly. My surname is Hollingshead. It is spelled H-O-L-L-I-N-G-S-H-E-A-D. First name is Sandra. You won't have too many other clients with a surname like that. That's true. Amusingly enough, you might exactly have one. And she and I, they share the same first name. Sorry, surname. But her first name is, I believe, Samantha. So I can't even say I'm the only S. Hollingshead in the phone book anymore. The personage of ill repute. Okay. I think she's a relative. Not a norm, but she's a relative. We're a little bit on the ground. Anyway. Okay. I had an outstanding balance. Well, actually larger than I would have expected. However, I made a payment today of $395.86. It is done via online banking. And the code of reference number is 224988. Now, once I checked my bill, I discovered that wasn't enough. So I made another payment of $124.56. I've made that payable on Wednesday, because that's when I get paid. And the reference number on that is 117464. And then to make certain that you guys get paid on the 31st, I set up another payment of 119.71. That is the 31st of January. And unfortunately, I did not take down all of the reference number. I have the first five digits, and I missed the last two. But it is... I'm just wondering if I can get it. No, that isn't... Hang on just a second. I will go back into my banking app and get you the last couple of digits. I should have checked before I phoned. One of these days, I am going to... As my mother used to so lovingly say, I'm going to put my brain in park. I usually put my brain in park and my mouth in drive. She wonders why I had a self-esteem issue. Okay. Now, I don't want to learn any more. Oh, there it is. Review. Cancel payments. Let's see. There it is. Now, how do I find that out? Okay. I don't want to cancel it. Ah. I'm having a hard time finding my last reference number. Will you trust me on this, or do I have to actually work hard enough to find it? No, it's okay. It's absolutely okay. I'll just make a note on your account. That's been set up to go through on the 31st of January. Wait a second. Let's see. I used to be able to get this information, but the bank and I have not been on speaking terms lately. I wish they'd let somebody closer from my account. No, I'm sorry. I have... I cannot easily find it because, of course, I'm looking for it. Oh, let's just give this one more try. Maybe it will go in this way. No, I can't find it. Oh, I don't understand that. Maybe because it hasn't gone through, I can't find it. Anyway, I'm sorry, but at least I can tell you it is on its way. If I find the number, I will phone back and one of your colleagues can complete the file. Thank you very much for putting up with me. I'm sorry to be so disorganized. I've only had one cup of coffee so far this morning. That means that I've woken up at least an extra brain. Thank you very much. You're welcome, Sandra. Have a good day. Bye-bye. Bye-bye.",
          understanding: {
            summary: "The call involved Sandra Hollingshead, a customer of Hydro Ottawa, contacting the customer service to update her account payments. Sandra humorously admitted to having been negligent but claimed to have rectified her situation within the last ten minutes. She provided detailed information about her payments to clear an outstanding balance: First payment: $395.86 made via online banking with reference number 224988. Second payment: $124.56, scheduled for her next payday, Wednesday, with reference number 117464. Third payment: $119.71, set for January 31st, though she initially failed to provide the complete reference number due to difficulties accessing her banking app during the call. Sandra made attempts to retrieve the last payment's reference number live during the call but faced challenges with her banking app. Despite her apology for the disorganization and her light-hearted commentary on her personal quirks and family anecdotes, the agent, Maggie, reassured Sandra that it was okay and made a note of the scheduled payment on Sandra's account. The call ended with Sandra expressing gratitude for the agent's patience and a mutual exchange of goodbyes.",
            category: "Payment: The primary focus was on Sandra making multiple payments to cover her outstanding balance. Account Maintenance: Sandra's actions and the conversation revolved around updating her account status with new payment information. Complaint & Escalations: While not a direct complaint, Sandra's difficulties with her banking app and her self-described disorganization might fall into this category, albeit very lightly and humorously.",
            sentiment: "The overall sentiment of the call was positive and light-hearted. Despite Sandra's self-admitted disorganization and initial negligence regarding her account, she approached the situation with humor and gratitude. The agent, Maggie, maintained a professional and accommodating demeanor, ensuring that the customer felt heard and assisted.",
            issue: "Sandra had an outstanding balance and made multiple payments to rectify this. She had trouble providing the complete reference number for her last payment due to issues with her banking app.",
            resolution: "Maggie, the agent, noted Sandra's payments and scheduled dates without requiring the final reference number, thus resolving the immediate concern of updating the payment information on Sandra's account.",
            recommendation: "It would be beneficial for Hydro Ottawa to consider providing a more streamlined process for customers to update payment information or inquire about account statuses. Additionally, ensuring that customer service representatives are trained to handle similar situations with empathy and efficiency, as demonstrated by Maggie, is crucial.",
            performance: "Maggie demonstrated excellent customer service skills by patiently listening to Sandra's situation, reassuring her that the lack of the complete reference number was not a problem, and making the necessary updates to Sandra's account. Maggie's approach was empathetic, accommodating, and efficient, which likely contributed to the positive sentiment and resolution of the call.",
            rating: 0
          }
        },
        {
          id: 'F260103210446V1U2300062',
          name: 'Mehmet',
          image: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2Fmehmet.webp?alt=media&token=fb1865ee-bc8f-44d5-b06b-528d8ce52f35',
          recording: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2FF260103210446V1U2300062.mp3?alt=media&token=ffad626c-367b-43b6-884f-3983dcfe83d1',
          description: 'The caller needed to set up a new utility account due to moving and was unfamiliar with the billing options.',
          transcription: "Good afternoon. Thank you for calling Hydro Ottawa. This is Ashley. How may I help you? Good afternoon, Ashley. How are you doing? I'm doing well. And yourself? Thank you. I am also. I am calling for, we are moving the new address, but it's also first time opening the account Hydro. Okay. So you're first time with us. All right. Do you have a form of a driver's license or a passport number? I have driver's license number. And for this property, will you be the owner or renting? It's tenant. You're a tenant. Okay. Yeah. Do you want me to start to license number? No. One moment, please. I just wanted to confirm that you have it. Just one moment, please. Can I start by getting the address that you're moving into? It's 1-8-3, Pre-Swan Road. Pre-Swan, it's P, like pen, S, like ring, E, like employee, S, like Sunday, L, like lady, A, like Apple, N, like Nike, D, like both. Perfect. And which unit are you moving into? Unit 1. The postal code is K1K2C1. Perfect. One moment. Your name, please, last name and first name. The first name, Mehmet Hussein, M, like Monday, E, like employer, H, like holiday, M, like Monday, E, like employer, T, like took. The second name is Hussein, H, it's holiday, U, as umbrella, S, like Sunday, E, like employer, Y, like yellow, I, and N, like Nike. Mehmet Hussein. And last name, it's E, K, I, C, I, employer, K, I, Charlie, I. Thank you. And your phone number, please? Phone number is, just a moment, 438-596-7672. Okay, birthday? Birthday is 11 of May, 1995. 11 of May, 11, 05, 1995. And your driver's license? Driver's license? Yes, the number, please. Yeah, it's starting E48685374950511. Perfect. And the email? The email is S, like Sunday, E, like employer, Y, like yellow, I, and like Nike199502 at gmail.com. Thank you so much. And your future language preference, English or French? English, please. Just a moment. Your move-in date? From today, we can start, yeah. And again, it's 183 Pressland Road, Apartment 1, right? Yeah, 183 Pressland Road, Unit 1, Postal Code K1K2C1. And how would you like for your hydro consumption to be charged, tiered or time of use? Time of use is where we charge you three different rates throughout the day, off-peak, mid-peak, on-peak. Tiered is one flat rate up to 1,000 kilowatts. After 1,000, it's a different rate. I don't have any idea for starting for newcomers. That will be for us, okay. Okay, so with that being said, you can start with the tiered, and after three months or so, you can give us a call back. We can review your usage and see which is the better option, and then you can change it. And you don't have to pay anything to change. Okay. It's good. Perfect. May I have your landlord name and number? Landlord name, just a moment. Yeah. The name is, it's actually company, but the owner, the owner, the owner of the company. Yeah. It's company. In the contract, it's company. It's solid, home, investments, ink. It's Sunday, like oil, like lady, I, D like door, solid, home, H-O-M-E, investments, I-N-V-E, S-T-M-E-N-T-S, investments, I-N-V, that's it. And do you have a phone number? Yeah. I have a phone number too, just a moment, just a moment. It's 613-286-4844. I have another number too, if you want it. No, that one number is fine, thank you. Okay. Okay, so I've gone ahead and gotten the account set up in your name starting for today. For the account, you're able to go online, register for online, and enroll for e-billing. Automatic withdrawal as well as equal billing is also available for the account. The first bill in your name will be in six weeks, and included in the first bill is a $28 setup fee. Yeah. Okay, any other questions or concerns? No. Thank you. Perfect, thank you. You did great. Thank you so much. I appreciate that. Thank you. Have a good day. You too. Bye.",
          understanding: {
            summary: "The caller, Mehmet Hussein Ekici, contacted Hydro Ottawa to set up a new account for electricity services at a new address, indicating they are moving and it's their first time opening an account with Hydro Ottawa. Mehmet provided personal information, including a driver's license number, contact details, and chose English for language preference. They are moving into Unit 1 at 183 Pressland Road, with the postal code K1K2C1, and identified as a tenant. The move-in date is set for the same day as the call. Mehmet was unsure about choosing between the tiered and time-of-use billing options. Ashley, the Hydro Ottawa representative, explained both options and suggested starting with the tiered rate, with the option to review and change the billing plan after three months without any cost. Mehmet agreed to this arrangement. Additionally, Mehmet provided the landlord's details, which is a company named Solid Home Investments Inc., along with a contact number. Ashley confirmed the account setup starting from the call day, explained the online registration process for e-billing, automatic withdrawal, and equal billing options. The first bill is expected in six weeks, including a $28 setup fee. The call concluded with no further questions or concerns from Mehmet.",
            category: "Moves: The primary focus of the call was on setting up a new account due to moving. Account Maintenance: The call involved setting up a new account and choosing billing options. Billing: Discussed billing options (tiered or time of use) and the setup fee.",
            sentiment: "The overall sentiment of the call was positive. Mehmet was cooperative and provided all necessary information promptly. Ashley was helpful, providing clear explanations and options, leading to a satisfactory setup process for Mehmet. The appreciation expressed by Mehmet at the end of the call further indicates a positive interaction.",
            issue: "The caller needed to set up a new utility account due to moving and was unfamiliar with the billing options.",
            resolution: "Ashley successfully set up the account in Mehmet's name, provided detailed explanations of the billing options, and recommended starting with the tiered rate with flexibility to change later. Ashley also informed Mehmet about online account management options and the setup fee.",
            recommendation: "To enhance customer experience, Hydro Ottawa could consider sending a welcome package or email to new customers with detailed information about billing options, tips on energy savings, and how to navigate their online account. This proactive approach could help new customers like Mehmet feel more informed and engaged right from the start.",
            performance: "Ashley demonstrated excellent customer service skills. She was patient, attentive, and provided clear and concise information throughout the call. Ashley's suggestion to start with a tiered billing option and allow for a future review based on actual consumption shows a customer-centric approach. Her performance was efficient, ensuring all necessary details were covered without overwhelming the caller. Ashley's professionalism and helpfulness likely contributed to the positive sentiment expressed by Mehmet at the end of the call.",
            rating: 0
          }
        },
        {
          id: 'F260111131527V1U2300076',
          name: 'Tracy',
          image: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2Ftracy.webp?alt=media&token=4fdf0513-d563-4735-af86-52de6c8fafe2',
          recording: 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/CallClassifier%2FF260111131527V1U2300076.mp3?alt=media&token=ce5d5782-4fa3-4f4b-8cf0-aa1633bae357',
          description: 'The primary issues include a billing inquiry for a new account that supposedly owes nothing and significant communication difficulties due to a poor phone connection.',
          transcription: "Thank you for calling Hydro Ottawa. My name is Jan. How can I help you today? Hi, Jan. It's Tracy calling from Ontario Works. How are you? I'm doing well, Tracy. How are you? Good. Thank you. My extension is 23911. One moment. All right. And can you please confirm the account number you're inquiring about today? Okay. Well, I can't do that because she's given me an e-mail, but part of the number is not on there. I can give you her address. I was calling you guys yesterday, and apparently the gentleman told me there was nothing owing on the account because it was new, but she just supplied me with a bill of over $300. So the address is 3873 Mavis Street. Okay. And it belongs to Hanan Antabi. Okay. It appears as though I'm just having some psychopathy, so I'm just going to see if I can resolve this. I'll be back shortly. Okay. Okay. Okay. Okay. Okay. Okay. Okay. Hi there. Thank you for holding. Okay. So, I was able to pull up the account. Sure. And... So, the account that I have is... ...is valid. Go ahead. I have 731408. Go ahead. And then the rest of the numbers are blocked out. Okay. Could you repeat the customer's first name? Yes. Let me scroll up to the top here. It should be Hanan and Tabby. Okay. So, their account... Actually, they have a different account number. So, their account is 5513484852. 5513484852? Yes. 53484852. Yes. Okay. Yeah. Your phone's cutting out every couple of seconds. If I want to apply for OESP, are there any more numbers to that account? That's only 9. 1, 2, 3, 4, 5, 6, 7. Okay. What's the remaining 10? U77. Sorry, what's that? 9985277. No. Your phone is cutting out every couple of seconds. Let me see if I can restore that. Hi there. Is that better? Can you hear me now? Yeah, I can hear you, but there's an echo every time I speak. It echoes a couple of seconds later. I have 5513484852. I just forgot that. So, the account number is 5515813... Oh, my God. Okay, hello? Hello? U77. I'm not hearing a thing. Yes. I'm getting, like, two numbers, and then it stops, and then you stop talking. Is there something wrong with your phone? Is there an email that we can email you guys at? Let me see if I can pass you on to another... Okay. I can see if I can pass you on to another agent for a moment. Okay.",
          understanding: {
            summary: "The call involves Tracy from Ontario Works inquiring about a Hydro Ottawa account on behalf of Hanan Antabi. Tracy begins the call by explaining that she does not have the full account number, only the customer's address and a bill exceeding $300 for what is supposed to be a new account with no charges. The agent, Jan, attempts to locate the account using the partial information provided. After experiencing some technical difficulties, Jan manages to pull up the correct account but faces communication issues due to a poor phone connection, including cutouts and an echo. The conversation involves attempts to clarify the account number and discuss applying for the Ontario Electricity Support Program (OESP), but the communication issues persist. The call concludes with Jan attempting to transfer Tracy to another agent due to the ongoing technical problems.",
            category: "Billing: The inquiry involves a bill of over $300 for a supposedly new account. Account Maintenance: Involves verifying and discussing details of Hanan Antabi's account. Social Assist: Mention of applying for the OESP. Issue with Communication: Due to phone connection problems (not a predefined category but relevant here).",
            sentiment: "The sentiment throughout the call appears to be neutral to slightly negative, primarily due to technical issues (poor phone connection) impacting communication. The caller remains patient but the frustration is implied by the situation's context, not overtly expressed through anger or dissatisfaction.",
            issue: "The primary issues include a billing inquiry for a new account that supposedly owes nothing and significant communication difficulties due to a poor phone connection.",
            resolution: "Jan attempts to resolve the billing inquiry by locating the correct account but is hindered by the communication issues.",
            recommendation: "It is recommended to address the technical issues affecting phone communication to improve customer service. Further, a thorough review of the account in question is advised to clarify and resolve the billing discrepancy. Offering alternative communication channels, such as email or a customer portal, may also be beneficial for cases where phone communication is problematic.",
            performance: "Jan demonstrates patience and a willingness to assist despite technical difficulties. However, the effectiveness of the support provided is limited by external factors (communication issues). Jan's attempt to transfer the call to another agent in light of these issues indicates a proactive approach to resolving the situation. Improvements could include quicker identification of communication problems and offering alternative solutions such as confirming details via email or a secure online portal.",
            rating: 0
          }
        },
      ],
      pipeline: [
        {
          title: 'Fetch Call Recording',
          subtitle: 'Google Cloud Function',
          description: 'Call recordings are located in Snowflake. This function fetches the recording audio file based on the call ID.',
          colour: 'blue',
          text_colour: 'blue--text',
          icon: '',
        },
        {
          title: 'Convert Audio Format',
          subtitle: 'Google Cloud Platform Extension',
          description: 'Telstrat provides call recordings in a specific WAV format which is not supported by Whisper. This extension converts the audio file to MP3.',
          colour: 'red',
          text_colour: 'red--text',
          icon: '',
        },
        {
          title: 'Transcribe',
          subtitle: 'OpenAI Whisper',
          description: 'The audio file is transcribed to text using OpenAI Whisper which is a speech-to-text product.',
          colour: 'yellow darken-2',
          text_colour: 'text--darken-2 yellow--text',
          icon: '',
        },
        {
          title: 'Large Language Model Analysis',
          subtitle: 'Google Gemini 1.0 Pro',
          description: 'The transcribed text is analyzed using Google Gemini 1.0 Pro to understand the sentiment, issue, resolution, and recommendation.',
          colour: 'blue',
          text_colour: 'blue--text',
          icon: '',
        },
        {
          title: 'Record Data',
          subtitle: 'Google Cloud Platform (Firestore)',
          description: 'The processed data is recorded in Firestore for future reference and analysis.',
          colour: 'green',
          text_colour: 'green--text',
          icon: '',
        },
        {
          title: 'Response',
          subtitle: 'Google Cloud Platform Function',
          description: 'The status of the process is sent back to the client.',
          colour: 'red',
          text_colour: 'red--text',
          icon: '',
        },
      ],
    }),
    filters: {
      joinArray(value) {
        return Array.isArray(value) ? value.join(', ') : 'N/A';
      }
    },
    methods: {
      async loadExplorerData() {
        this.explorerLoadingState = 1;
        await this.load(true); // Initial load only, fetching the first 500 records
        this.filteredItems = this.items;
        this.explorerLogDocId = null; // Reset explorer log document ID for a new search
        this.explorerLoadingState = 2;
      },
      async loadCategoryData() {
        this.categoriesLoadingState = 1;
        await this.loadCategories();
        this.categoriesLoadingState = 2;
      },
      async loadJourneyData() {
        this.journeysLoadingState = 1;
        await this.loadJourneys();
        this.journeysLoadingState = 2;
      },
      async load(initialLoad = false) {
        console.log('Loading initiated...');
        this.explorerLoading = true;
        this.startTimer();
        this.explorerLoadingState = 1;
        try {
          // Fetch the signed URL from the Cloud Function
          const urlResponse = await fetch('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/cs_get_calls');
          if (!urlResponse.ok) {
            throw new Error('Failed to fetch signed URL');
          }
          const urlData = await urlResponse.json();
  
          // Use Fetch API with a ReadableStream to monitor progress
          const response = await fetch(urlData.url);
          if (!response.ok) {
            throw new Error('Failed to fetch call data');
          }
  
          const contentLength = parseInt(response.headers.get('content-length'), 10);
          const totalBytes = 58787728;  // Set the total bytes manually since you know it
          let loadedBytes = 0;
          const reader = response.body.getReader();
          const stream = new ReadableStream({
            start: (controller) => {
              const push = () => {
                reader.read().then(({ done, value }) => {
                  if (done) {
                    controller.close();
                    return;
                  }
                  loadedBytes += value.length;
                  const progress = (loadedBytes / totalBytes) * 100;
                  // console.log(`Progress: ${progress.toFixed(2)}%`);
                  // this.progress = progress;  // Update the progress bar
                  controller.enqueue(value);
                  push();
                });
              };
              push();
            }
          });
  
          const newResponse = new Response(stream);
          const data = await newResponse.json();
  
          // Assuming the data is an array of call records
          this.items = data;
          this.processed = data.length;
  
          this.explorerLoading = false;
          this.stopTimer();
          this.executeSearch();
  
          if (initialLoad) {
            return;
          }
  
          if (!this.stopLoadingFlag && data.length > 0) {
            this.load();
          }
          
        } catch (error) {
          console.error('Error loading call data:', error);
          this.explorerLoading = false;
          this.stopTimer();
        }
      },
      executeSearch() {
        console.log('search')
        this.filteredItems = this.items.filter(item => {
          const searchTerm = this.search.toLowerCase();
          if (!searchTerm) return true;
          return (typeof item.id === 'string' && item.id.toLowerCase().includes(searchTerm) || 
                  typeof item.date === 'string' && item.date.toLowerCase().includes(searchTerm) || 
                  typeof item.category === 'string' && item.category.toLowerCase().includes(searchTerm) || 
                  typeof item.summary === 'string' && item.summary.toLowerCase().includes(searchTerm));
        });
      },
      startContinuousLoading() {
        this.stopLoadingFlag = false;
        this.load();
      },
      stopLoading() {
        this.stopLoadingFlag = true;
        this.explorerLoading = false;
        this.stopTimer();
      },
      async openCallDialog(item) {
        console.log(item.id)
        if (!item) return false
        // Fetch the selected item from the Firebase collection
        const db = getFirestore();
        const callsRef = collection(db, 'cs_customer_calls');
        const callDocRef = doc(callsRef, item.id);
        const callDoc = await getDoc(callDocRef);
        this.selectedItem = callDoc.data();
        this.callDialog = true;
      },
      async sendQuery() {
        if (!this.userQuery) return;
  
        this.assistantLoading = true;
        this.startTimer();
        this.history.push({ type: 'user', content: this.userQuery });
  
        // Log the query before sending the request
        await this.logQuery('assistant');
  
        try {
          let endpoint = ''
          if (this.environment == 'local') endpoint = 'http://localhost:5000/hydro-ottawa-ai/northamerica-northeast1/geminicalls';
          if (this.environment == 'development') endpoint = 'https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/geminicalls';
          if (this.environment == 'production') endpoint = 'https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/geminicalls';

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userMessage: this.userQuery, sessionId: this.sessionId })
          });
          const data = await response.json();
          if (data.success) {
            const replacedContent = data.html.replace(/F\d{12}V\d{1}U\d{7}/g, (match) => {
              console.log('Match:', match);
              return `<a href="#" class="call-link" data-id="${match}">${match}</a>`;
            });
            this.history.push({ type: 'model', content: replacedContent, citations: data.citations });
          } else {
            this.history.push({ type: 'model', content: 'Error: ' + data.response });
          }
        } catch (error) {
          this.history.push({ type: 'model', content: 'Error: ' + error.message });
        } finally {
          this.assistantLoading = false;
          this.stopTimer();
          // Log the query again after receiving the response or handling an error
          await this.logQuery('assistant');
        }
        this.userQuery = '';
      },
      async logQuery(type) {
        const db = getFirestore();
        const logsRef = collection(db, 'queries');
  
        try {
          if (type === 'explorer') {
            const logDoc = {
              user: this.user.email,
              type,
              query: this.search || '', // Ensure query is not undefined
            };
            await addDoc(logsRef, logDoc);
          } else if (type === 'assistant') {
            const logDoc = {
              user: this.user.email,
              type,
              history: this.history.map(item => item.content), // Flattening the history array to just contain the content
            };
            if (this.assistantLogDocId) {
              // console.log('assistant update', this.assistantLogDocId, logDoc);
              // Update existing log document
              const logDocRef = doc(logsRef, this.assistantLogDocId);
              await setDoc(logDocRef, logDoc, { merge: true }); // Use merge to only update fields
            } else {
              // console.log('assistant create', logDoc);
              // Create new log document
              const logDocRef = await addDoc(logsRef, logDoc);
              this.assistantLogDocId = logDocRef.id;
            }
          }
        } catch (error) {
          console.error('Error logging query:', error);
        }
      },
      startTimer() {
        this.timer = 0;
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.timerInterval = setInterval(() => {
          this.timer++;
        }, 1000);
      },
      stopTimer() {
        if (this.timerInterval) clearInterval(this.timerInterval);
      },
      async startConversation() {
        this.history = [];
        this.sessionId = Date.now().toString(36) + Math.random().toString(36); // Generate a new session ID for a new conversation
        this.assistantLogDocId = null; // Reset assistant log document ID for a new conversation
      },
      initialize() {
        // this.load(true); // Initial load only, fetching the first 500 records
        // this.filteredItems = this.items;
        // this.explorerLogDocId = null; // Reset explorer log document ID for a new search
        // this.loadCategories();
        // this.loadJourneys();
        // this.fetchData()
      },
      async loadCategories() {
        console.log('Loading categories...');
        const db = getFirestore();
        const categoriesRef = collection(db, 'cs_call_categories');
        const categoriesSnapshot = await getDocs(categoriesRef);
  
        let categories = categoriesSnapshot.docs.map(doc => ({
          id: doc.id, // Document ID
          ...doc.data() // Spread all other fields (name, calls, count)
        }));
  
        // Sort this.categories by count in descending order
        categories.sort((a, b) => b.count - a.count);
        this.categories = categories;
  
        console.log('Done.');
      },
      async loadJourneys() {
        console.log('Loading journeys...');
        const db = getFirestore();
        const journeysRef = collection(db, 'cs_call_journeys');
        const journeysSnapshot = await getDocs(journeysRef);
  
        let journeys = journeysSnapshot.docs.map(doc => ({
          id: doc.id, // Document ID
          ...doc.data() // Spread all other fields (name, calls, count)
        }));
  
        // Sort this.categories by count in descending order
        journeys.sort((a, b) => b.count - a.count);
        this.journeys = journeys;
  
        console.log('Done.');
      },
      generateImage() {
        const endpoint = 'http://localhost:5000/hydro-ottawa-ai/northamerica-northeast1/imagen';
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: 'a successful business man' })
        })
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok ' + response.statusText);
            }
            return response.json();
          })
          .then(data => {
            console.log(data);
          })
          .catch(error => {
            console.error('Error:', error);
          });
      },
      improvements(value) {
        if (this.selectedItem) {
          let x = ''
          value.forEach(item => {
            x += "Suggestion: " + item.improvement + ', Logic: ' + item.reasoning + '; '
          });
          return x;
        }
      },
      showDialog(index) {
        console.log('cool')
        this.selectedUseCase = this.usecases[index]
        this.dialog = true
      },
      playRecording() {
        this.player = new Audio(this.selectedUseCase.recording)
        this.player.play()
      },
      stopRecording() {
        this.player.pause()
      },
      fetchData() {
        const db = getFirestore();
        const transcriptionsRef = collection(db, 'transcriptions');
        const first = query(transcriptionsRef, limit(this.itemsPerPage));
        getDocs(first).then((documentSnapshots) => {
          this.lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
          this.items = documentSnapshots.docs.map((doc) => doc.data());
          this.loadingDataset = false
        }).catch((error) => {
          console.error('Error fetching data:', error);
        });
      },
      prevPage() {
        if (this.currentPage === 1) return; // Already on the first page
        this.currentPage -= 1;
        const db = getFirestore();
        const transcriptionsRef = collection(db, 'transcriptions');
        const first = query(transcriptionsRef, limit(this.itemsPerPage));
        getDocs(first).then((documentSnapshots) => {
          this.lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
          this.items = documentSnapshots.docs.map((doc) => doc.data());
        }).catch((error) => {
          console.error('Error fetching data:', error);
        });
      },
      nextPage() {
        const db = getFirestore();
        const transcriptionsRef = collection(db, 'transcriptions');
        const next = query(transcriptionsRef, startAfter(this.lastVisible), limit(this.itemsPerPage));
        getDocs(next).then((nextDocumentSnapshots) => {
          this.lastVisible = nextDocumentSnapshots.docs[nextDocumentSnapshots.docs.length - 1];
          this.items = [...this.items, ...nextDocumentSnapshots.docs.map((doc) => doc.data())];
          this.currentPage += 1;
        }).catch((error) => {
          console.error('Error fetching data:', error);
        });
      },
      viewTranscript(title, text) {
        this.transcriptionDialog = true
        this.transcriptionTitle = title
        this.transcriptionText = text
      }
    },
    filters: {
      truncate(string) {
        let value = 80;
        return string?.length > value ? string.slice(0, value) + "..." : string;
      },
      joinArray(value) {
        return Array.isArray(value) ? value.join(', ') : 'N/A';
      },
    },
    watch: {
      user() {
        this.startConversation();
        this.initialize();
      },
      $route(to, from) {
        this.startConversation();
        this.initialize();
      },
      transcriptionDialog() {
        const player = this.$refs.transcriptPlayer;
        if (player) {
          player.pause();
          player.currentTime = 0; // Reset the time
        }
      }
    },
    mounted() {
      // Load initial 500 records
      this.initialize()
      this.$nextTick(() => {
        document.addEventListener('click', (event) => {
          if (event.target.classList.contains('call-link')) {
            event.preventDefault();
            const callId = event.target.getAttribute('data-id');
            this.openCallDialog({ id: callId });
          }
        });
      });
    }
  })
</script>
  
<style lang="scss" scoped>
  .message {
    .system-message {
      max-width: 80%;
      background: #f0f0f0;
      color: black;
      border-radius: 10px;
      text-align: left !important;
    }
    .user-message {
      max-width: 80%;
      background: #4caf50;
      color: white;
      border-radius: 10px;
    }
  }
  .citations {
    font-size: 0.875rem;
    color: #757575;
  }
</style>